// Build marker 2026-09-07: force a fresh SWA deploy (previous push's web deploy
// did not publish; live bundle was missing the phone path-gating fix).
import React from 'react';
import ReactDOM from 'react-dom';
import { Avatar, Icon, Popover, fmtTime, getUser } from './components.jsx';
import { ContactsView } from './contacts.jsx';
import { EvolutionMattersView, EvolutionMattersStandalone } from './evolution-matters.jsx';
import { FirmAppsView } from './firm-apps.jsx';
import { ALL_COLUMNS, BUILTIN_VIEWS, CATEGORIES, KB_ARTICLES, LOCATIONS, MAILBOX, ME_ID, PRIORITIES, REQUESTERS, STATUSES, SUBCATS, TICKETS, USERS, setMeId } from './data.jsx';
import { FeedbackView } from './feedback.jsx';
import { HomeView } from './home.jsx';
import { RequestsPortal, UserRequestsAdmin, NdOAuthCallback } from './requests.jsx';
import { WsmPoliciesView } from './wsm.jsx';
import { TapView } from './tap.jsx';
import { DuoPassesView } from './duo-passes.jsx';
import { KBAttachmentsBlock, KBView } from './kb.jsx';
import { SettingsView } from './settings.jsx';
import { StatsView } from './stats.jsx';
import { TasksView } from './tasks.jsx';
import { TicketsView } from './tickets.jsx';
import { TweakColor, TweakRadio, TweakSection, TweakToggle, TweaksPanel, useTweaks } from './tweaks-panel.jsx';
import { RotaView } from './rota.jsx';
import { WallboardView } from './wallboard.jsx';
// L&T IT Helpdesk - main app

// ─── API layer ───────────────────────────────────────────────────────────────
// All four .jsx files share window.IT_AUTH.api (defined in index.html) so the
// bearer-token + fetch + JSON pattern lives in exactly one place.
const api = (method, path, body) => window.IT_AUTH.api(method, path, body, 'api');

// Map a Redux-style action to the appropriate API call.
// Returns a Promise (or null for UI-only actions that need no persistence).
const syncAction = (action, state) => {
  const { id } = action;
  switch (action.type) {
    case "assign":
      return api("PATCH", `/tickets/${id}`, { assigneeId: action.assignee });
    case "bulkAssign":
      return Promise.all([...state.selected].map(tid =>
        api("PATCH", `/tickets/${tid}`, { assigneeId: action.assignee })));
    case "setStatus":
      return api("PATCH", `/tickets/${id}`, { status: action.status });
    case "bulkStatus":
      return Promise.all([...state.selected].map(tid =>
        api("PATCH", `/tickets/${tid}`, { status: action.status })));
    case "setPriority":
      return api("PATCH", `/tickets/${id}`, { priority: action.priority });
    case "renameTicket":
      return api("PATCH", `/tickets/${id}`, { subject: action.subject });
    case "setCategory":
      return api("PATCH", `/tickets/${id}`, { category: action.category, subcat: SUBCATS[action.category]?.[0] || "" });
    case "setSubcat":
      return api("PATCH", `/tickets/${id}`, { subcat: action.subcat });
    case "setLocation":
      return api("PATCH", `/tickets/${id}`, { location: action.location });
    case "setRequester":
      return api("PATCH", `/tickets/${id}`, { requesterEmail: action.requester, requesterName: action.requesterName || null });
    case "convertEmailToTicket": {
      const email = state.mailbox.find(m => m.id === action.emailId);
      if (!email) return null;
      const emailAddr = (email.from.match(/<(.+)>/) || ["", email.from])[1].toLowerCase() || email.from.toLowerCase();
      const fromName = email.from.replace(/<.+>/, "").trim();
      const emailDate = new Date(Date.now() + email.received * 60000).toISOString();
      return api("POST", "/tickets", {
        subject: email.subject.replace(/^(re|fw|fwd):\s*/i, ""),
        priority: "medium", category: null, subcat: null,
        location: "Helpdesk (Email)",
        requesterEmail: emailAddr, requesterName: fromName,
        description: email.body || "",
        createdAt: emailDate,
      });
    }
    case "resolve":
      return api("PATCH", `/tickets/${id}`, { status: "resolved", solution: action.solution });
    case "assignAndResolve":
      return api("PATCH", `/tickets/${id}`, { assigneeId: action.assignee, status: "resolved", solution: action.solution });
    case "saveSolution":
      return api("PATCH", `/tickets/${id}`, { solution: action.solution });
    case "addMessage":
      return api("POST", `/tickets/${id}/messages`, {
        fromEmail: action.msg.from, fromName: action.msg.from,
        body: action.msg.body, isInternal: action.msg.internal,
      });
    case "editNote":
      return api("PATCH", `/tickets/${action.ticketId}/messages/${action.messageId}`, { body: action.body });
    case "deleteNote":
      return api("DELETE", `/tickets/${action.ticketId}/messages/${action.messageId}`);
    case "trashTicket":
      return api("PATCH", `/tickets/${id}`, { trashed: true });
    case "trashTickets":
      return Promise.all([...state.selected].map(tid =>
        api("PATCH", `/tickets/${tid}`, { trashed: true })));
    case "restoreTickets":
      return Promise.all([...state.selected].map(tid =>
        api("PATCH", `/tickets/${tid}`, { trashed: false })));
    case "restoreTicket":
      return api("PATCH", `/tickets/${id}`, { trashed: false });
    case "emptyTrash":
      return api("DELETE", "/tickets/empty-trash");
    case "createTicket": {
      const staffMember = (window.STAFF_LIST || []).find(s => s.mail === action.requester);
      return api("POST", "/tickets", {
        subject: action.subject, priority: action.priority || "medium",
        category: action.category, subcat: action.subcat,
        location: action.location || null,
        requesterEmail: action.requester,
        requesterName: staffMember?.displayName || null,
        description: action.body,
        assigneeId: action.assignee || null,
      });
    }
    case "mergeDuplicates": {
      const primary = state.tickets.find(t => t.id === action.primaryId);
      if (!primary) return null;
      // sourceIds MUST come from the caller (e.g. the bulk-select toolbar).
      // Earlier this action recomputed sources from "any other ticket with the
      // same subject", which silently pulled in unselected tickets. Bug:
      // merging A+B with the same subject as unselected C would also merge C.
      // Now we use only what the user explicitly chose, and defensively
      // ignore anything that isn't a valid current ticket.
      const sourceIds = (action.sourceIds || [])
        .filter(id => id !== action.primaryId && state.tickets.some(t => t.id === id && !t.duplicateOf));
      if (!sourceIds.length) return null;
      return Promise.all(
        sourceIds.map(id => api("POST", `/tickets/${action.primaryId}/merge`, { sourceId: id }))
      ).then(results => {
        // Return last merged result (primary ticket with all messages)
        return results[results.length - 1];
      });
    }
    case "splitMessage":
      return api("POST", `/tickets/${action.ticketId}/split-message`, { messageId: action.messageId });
    // ─── Categories (Settings page) ──────────────────────────────────────────
    case "createCategory":
      return api("POST", "/categories", { id: action.id, label: action.label, sortOrder: action.sortOrder });
    case "updateCategory":
      return api("PATCH", `/categories/${action.id}`, action.updates);
    case "deleteCategory":
      return api("DELETE", `/categories/${action.id}`);
    // ─── KB ──────────────────────────────────────────────────────────────────
    case "createArticle":
      return api("POST", "/kb", {
        title: action.title,
        category: action.category,
        tags: action.tags,
        summary: action.summary,
        body: action.body,
        status: action.status || 'draft',
      });
    case "updateArticle":
      return api("PATCH", `/kb/${action.id}`, action.updates);
    case "deleteArticle":
      return api("DELETE", `/kb/${action.id}`);
    case "toggleFavourite":
      return api("POST", `/kb/${action.id}/favourite`);
    default:
      return null; // navigate, select, toggleCheck, etc. - UI only, no persistence needed
  }
};

// ─────────────────────────────────────────────────────────────────────────────

// View ▸ column layout persistence. Validated against ALL_COLUMNS so a stale
// or corrupted save can never produce an unrenderable table.
const COLUMN_CONFIG_KEY = 'triage:ticket-columns:v1';
function restoreColumnConfig() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMN_CONFIG_KEY) || 'null');
    if (saved && Array.isArray(saved.columns)) {
      const valid = saved.columns.filter(id => ALL_COLUMNS.some(c => c.id === id));
      if (valid.length) return { tableView: saved.tableView || 'custom', columns: valid };
    }
  } catch (_) { /* corrupt save or private mode - fall through to default */ }
  return { tableView: 'default', columns: BUILTIN_VIEWS[0].columns.slice() };
}

const initialState = {
  // state.tickets is the CURRENT PAGE of the paginated list, not every
  // ticket in the DB. See ticketsMeta for total/page/size/hasMore, and
  // ticketsQuery for the filter/sort/search shape that was requested.
  // Rows here are slim: message bodies are populated for the first
  // message only. Detail-view hydration (below, "hydrateTicket") swaps
  // in the full messages array when a ticket is opened.
  tickets: [],
  ticketsMeta: { total: 0, page: 1, size: 50, hasMore: false, loading: false },
  ticketsQuery: {
    view: 'inbox',
    assignee: null,
    status: null,
    category: null,
    requester: null,
    q: '',
    sort: { col: 'requestTime', dir: 'desc' },
  },
  // Sidebar badge numbers - refreshed after every ticket mutation via a
  // cheap COUNT(*) endpoint. Previously computed by filtering the local
  // tickets array, which is no longer possible with pagination.
  counts: { all: 0, inbox: 0, mine: 0, unassigned: 0, resolved: 0, trash: 0 },
  // KB articles come from the API now; KB_ARTICLES is a [] placeholder in data.jsx
  // so existing references don't crash while the initial fetch is in flight.
  kbArticles: KB_ARTICLES,
  // Ticket categories - loaded from /api/categories on init. Defaults to the
  // built-in list in data.jsx until the API responds, so the first render
  // doesn't show an empty dropdown.
  categories: CATEGORIES,
  mailbox: MAILBOX,
  view: "inbox", // inbox | mine | unassigned | resolved | kb | stats
  selectedId: null,
  selected: new Set(),
  kbOpenId: null,
  newTicketOpen: false,
  newArticleOpen: false,
  editingArticleId: null,
  mailboxOpen: false,
  // Table view config - restored from localStorage so View ▸ column choices
  // survive reloads and navigation. Saved by the effect next to useReducer.
  ...restoreColumnConfig(),
};

// The subset of state.view values that correspond to a server-side ticket
// list. Anything not in here (kb, home, stats, rota, tasks-*, settings…)
// doesn't refetch tickets when navigated to.
const TICKET_LIST_VIEWS = new Set(['all', 'inbox', 'mine', 'unassigned', 'resolved', 'fa-ib', 'trash']);

// Per-view sticky status filter. Every list view remembers its OWN last
// status choice (Any status / Open / New / In Progress / Closed) across
// navigation and reloads; the default when you've never chosen one is Any
// status (null). Set My Tickets to Open and it stays Open on My Tickets;
// All tickets keeps its own independent choice. Persisted per-browser in
// localStorage. Loaded once at module init into a module-level cache the
// reducer reads (writes go through the persistence effect in App).
const STATUS_BY_VIEW_KEY = 'triage:ticket-status-by-view:v1';
const _stickyStatus = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(STATUS_BY_VIEW_KEY) || '{}');
    return (saved && typeof saved === 'object') ? saved : {};
  } catch (_) { return {}; }
})();
const readStickyStatus = (view) => _stickyStatus[view] ?? null;
const writeStickyStatus = (view, status) => {
  if (status) _stickyStatus[view] = status;
  else delete _stickyStatus[view];
  try { localStorage.setItem(STATUS_BY_VIEW_KEY, JSON.stringify(_stickyStatus)); }
  catch (_) { /* quota / private mode */ }
};
// Restore the destination view's remembered status when navigating to it.
// Both navigation paths (the navigate reducer and the URL/popstate sync)
// run through this, so sidebar click, reload and back/forward behave the
// same. The openOnly/resolved clamps applied AFTER this drop any saved
// status the destination view can't actually display.
const withStickyStatus = (query, view) => ({ ...query, status: readStickyStatus(view) });

function reducer(state, action) {
  switch (action.type) {
    case "set":
      return { ...state, ...action };
    case "navigate": {
      // Navigating to a ticket-list view resets pagination back to page 1
      // and updates ticketsQuery.view so the fetch effect refires with the
      // right server-side filter. Assignee/status/category filter chips
      // persist across views intentionally (they're a separate UI control).
      const next = { ...state, view: action.view, selectedId: null, selected: new Set(), kbOpenId: null };
      if (TICKET_LIST_VIEWS.has(action.view)) {
        // Also clear a status filter that the destination view would make
        // incompatible or invisible - e.g. status='resolved' set while
        // moving to 'inbox' (which excludes resolved) would give an empty
        // list with no filter chip to un-set. Same on 'unassigned'.
        // On 'resolved' the filter chip is hidden entirely, so anything
        // other than 'resolved' would be a silent trap.
        // Restore the destination view's remembered status choice, then
        // clamp anything it can't display (inbox/unassigned exclude Closed;
        // the resolved view is all-Closed and hides the chip).
        const nextQuery = withStickyStatus({ ...state.ticketsQuery, view: action.view }, action.view);
        const openOnly = action.view === 'inbox' || action.view === 'unassigned';
        if (openOnly && nextQuery.status === 'resolved') nextQuery.status = null;
        if (action.view === 'resolved' && nextQuery.status && nextQuery.status !== 'resolved') nextQuery.status = null;
        next.ticketsQuery = nextQuery;
        next.ticketsMeta  = { ...state.ticketsMeta, page: 1, loading: true };
      }
      return next;
    }
    case "select":
      return { ...state, selectedId: action.id };
    case "toggleCheck": {
      const s = new Set(state.selected);
      if (s.has(action.id)) s.delete(action.id); else s.add(action.id);
      return { ...state, selected: s };
    }
    case "clearChecks":
      return { ...state, selected: new Set() };
    case "checkAll":
      return { ...state, selected: new Set(action.ids) };
    case "ticketsPageLoaded": {
      // First page (or a full refetch) - replaces state.tickets outright.
      // Two things we need to be careful about here:
      //
      // 1. Preserve HYDRATED MESSAGES on tickets we've already fetched in
      //    full: /api/tickets returns slim rows (first-message body only),
      //    so a background poll refetch would otherwise wipe the follow-up
      //    bodies the detail view had loaded via hydrateTicket. Detected
      //    by "> 1 message AND at least one follow-up has a body" - the
      //    hydration invariant.
      //
      // 2. Preserve the DEEP-LINKED / currently-open ticket when it's
      //    NOT on the incoming page. Landing on /tickets/402 for a closed
      //    ticket sets state.view = 'inbox' (defaults to All open),
      //    hydrateTicket inserts #402, then this reducer fires with only
      //    the 8 open tickets and would drop #402 - leaving the detail
      //    view stuck on "Loading ticket…" forever. Solution: keep any
      //    ticket that's currently selected and isn't in the new rows.
      const hydratedMsgs = new Map();
      for (const t of state.tickets) {
        if (Array.isArray(t.messages) && t.messages.length > 1
            && t.messages.slice(1).some(m => m.body)) {
          hydratedMsgs.set(t.id, t.messages);
        }
      }
      const incomingIds = new Set(action.rows.map(t => t.id));
      const rows = action.rows.map(t =>
        hydratedMsgs.has(t.id) ? { ...t, messages: hydratedMsgs.get(t.id) } : t
      );
      // Rescue the selected ticket if the incoming page doesn't include it.
      if (state.selectedId && !incomingIds.has(state.selectedId)) {
        const stray = state.tickets.find(t => t.id === state.selectedId);
        if (stray) rows.push(stray);
      }
      return {
        ...state,
        tickets: rows,
        ticketsMeta: {
          total:   action.total,
          page:    action.page,
          size:    action.size,
          hasMore: action.hasMore,
          loading: false,
        },
      };
    }
    case "ticketsPageAppended":
      // "Load More" clicked - append the next page onto the current list.
      // Dedupe by id in case a background poll interleaves and delivers a
      // ticket that's already visible.
      return {
        ...state,
        tickets: (() => {
          const seen = new Set(state.tickets.map(t => t.id));
          const fresh = action.rows.filter(r => !seen.has(r.id));
          return [...state.tickets, ...fresh];
        })(),
        ticketsMeta: {
          total:   action.total,
          page:    action.page,
          size:    action.size,
          hasMore: action.hasMore,
          loading: false,
        },
      };
    case "setTicketsQuery":
      // Any filter/sort/search change. Resets to page 1 and flags loading -
      // the effect wired to state.ticketsQuery will refire and dispatch
      // ticketsPageLoaded when the response arrives.
      return {
        ...state,
        ticketsQuery: { ...state.ticketsQuery, ...action.patch },
        ticketsMeta:  { ...state.ticketsMeta, page: 1, loading: true },
      };
    case "ticketsLoading":
      return { ...state, ticketsMeta: { ...state.ticketsMeta, loading: !!action.loading } };
    case "setCounts":
      return { ...state, counts: { ...state.counts, ...action.counts } };
    case "hydrateTicket": {
      // Detail view fetched the full ticket (with all message bodies).
      // Merge it into whichever position it occupies in the current page,
      // or insert if it's a deep-link to a ticket not on the current page
      // (e.g. clicked a link from an email). Either way state.tickets ends
      // up containing the full row that the detail view is rendering from.
      const idx = state.tickets.findIndex(t => t.id === action.ticket.id);
      const nextTickets = idx >= 0
        ? state.tickets.map(t => t.id === action.ticket.id ? { ...t, ...action.ticket } : t)
        : [action.ticket, ...state.tickets];
      return { ...state, tickets: nextTickets };
    }
    case "assign":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, assignee: action.assignee, status: t.status === "new" && action.assignee ? "progress" : t.status, updated: 0} : t)
      };
    case "bulkAssign":
      return {
        ...state,
        tickets: state.tickets.map(t => state.selected.has(t.id) ? { ...t, assignee: action.assignee, status: t.status === "new" && action.assignee ? "progress" : t.status, updated: 0} : t),
        selected: new Set(),
      };
    case "renameTicket":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, subject: action.subject, updated: 0 } : t)
      };
    case "setStatus":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, status: action.status, updated: 0 } : t)
      };
    case "bulkStatus":
      return {
        ...state,
        tickets: state.tickets.map(t => state.selected.has(t.id) ? { ...t, status: action.status, updated: 0 } : t),
        selected: new Set(),
      };
    case "setPriority":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, priority: action.priority, updated: 0 } : t)
      };
    case "setCategory":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, category: action.category, subcat: SUBCATS[action.category]?.[0] || "", updated: 0 } : t)
      };
    case "setSubcat":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, subcat: action.subcat, updated: 0 } : t)
      };
    case "setLocation":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, location: action.location, updated: 0 } : t)
      };
    case "setRequester":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, requester: action.requester, updated: 0 } : t)
      };
    case "addMessage": {
      const newMsg = { ...action.msg };
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? {
          ...t,
          messages: [...t.messages, newMsg],
          updated: 0,
          status: t.status === "new" ? "progress" : t.status,
        } : t)
      };
    }
    case "editNote": {
      // Optimistic body swap - server confirms silently. If the API
      // rejects, the next hydration snaps back to server truth.
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.ticketId ? {
          ...t,
          messages: (t.messages || []).map(m =>
            m.id === action.messageId ? { ...m, body: action.body } : m
          ),
        } : t)
      };
    }
    case "deleteNote": {
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.ticketId ? {
          ...t,
          messages: (t.messages || []).filter(m => m.id !== action.messageId),
        } : t)
      };
    }
    case "resolve": {
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? {
          ...t,
          status: "resolved",
          solution: action.solution,
          closedAt: 0,
          updated: 0,
        } : t)
      };
    }
    case "assignAndResolve": {
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? {
          ...t,
          status: "resolved",
          assignee: action.assignee,
          solution: action.solution,
          closedAt: 0,
          updated: 0,
        } : t)
      };
    }
    case "saveSolution":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, solution: action.solution } : t)
      };
    case "mergeDuplicates": {
      const primary = state.tickets.find(t => t.id === action.primaryId);
      if (!primary) return state;
      // Mirror the syncAction logic: only remove the EXPLICIT sources the
      // caller passed, never every-ticket-with-same-subject.
      const sourceIds = (action.sourceIds || [])
        .filter(id => id !== action.primaryId);
      return {
        ...state,
        selectedId: action.primaryId,
        tickets: state.tickets.filter(t => !sourceIds.includes(t.id)),
      };
    }
    case "mergedTicketLoaded": {
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.ticket.id ? action.ticket : t),
      };
    }
    case "emailTicketCreated": {
      return {
        ...state,
        tickets: [action.ticket, ...state.tickets.filter(t => t.id !== action.localId)],
        mailbox: state.mailbox.map(m => m.id === action.emailId ? { ...m, ticketId: action.ticket.id } : m),
        selectedId: action.ticket.id,
        view: "inbox",
        mailboxOpen: false,
      };
    }
    case "splitMessage": {
      // Optimistically remove the message from the ticket
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.ticketId
          ? { ...t, messages: t.messages.filter(m => m.id !== action.messageId) }
          : t),
      };
    }
    case "splitMessageLoaded": {
      const updated = state.tickets.map(t => t.id === action.original.id ? action.original : t);
      // Add the new ticket if it's not already in the list
      const exists = updated.some(t => t.id === action.newTicket.id);
      return {
        ...state,
        tickets: exists ? updated : [action.newTicket, ...updated],
      };
    }
    case "trashTickets":
      return {
        ...state,
        tickets: state.tickets.map(t => state.selected.has(t.id) ? { ...t, trashed: true } : t),
        selected: new Set(),
      };
    case "trashTicket":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, trashed: true } : t),
        selectedId: null,
      };
    case "restoreTickets":
      return {
        ...state,
        tickets: state.tickets.map(t => state.selected.has(t.id) ? { ...t, trashed: false } : t),
        selected: new Set(),
      };
    // Single-ticket restore from the trash detail view. Mirrors
    // trashTicket - the old detail-view Restore button dispatched a bare
    // "set" (selection only) and never actually restored anything.
    case "restoreTicket":
      return {
        ...state,
        tickets: state.tickets.map(t => t.id === action.id ? { ...t, trashed: false } : t),
        selectedId: null,
      };
    case "emptyTrash":
      return { ...state, tickets: state.tickets.filter(t => !t.trashed) };
    case "openKB":
      return { ...state, view: "kb", kbOpenId: action.articleId };
    case "openNewArticle":
      return { ...state, newArticleOpen: true };
    case "closeNewArticle":
      return { ...state, newArticleOpen: false };
    case "openMailbox":
      return { ...state, mailboxOpen: true };
    case "closeMailbox":
      return { ...state, mailboxOpen: false };
    case "setView":
      return { ...state, tableView: action.viewId, columns: BUILTIN_VIEWS.find(v => v.id === action.viewId)?.columns.slice() || state.columns };
    case "setColumns":
      return { ...state, tableView: "custom", columns: action.columns };
    case "convertEmailToTicket": {
      const email = state.mailbox.find(m => m.id === action.emailId);
      if (!email) return state;
      const nextId = -Date.now();
      // Find sender among known requesters by email-ish match, else fallback
      const emailAddr = (email.from.match(/<(.+)>/) || ["", email.from])[1].toLowerCase();
      const requester = REQUESTERS.find(r => r.email.toLowerCase() === emailAddr)?.id || REQUESTERS[0].id;
      const newT = {
        id: nextId,
        subject: email.subject.replace(/^(re|fw|fwd):\s*/i, ""),
        status: "new",
        priority: "medium",
        category: "",
        subcat: "",
        location: "Helpdesk (Email)",
        requester,
        assignee: null,
        created: email.received,
        updated: email.received,
        messages: [{ from: requester, body: email.body, time: -email.received, internal: false }],
        solution: "",
        tags: ["from-email"],
      };
      return {
        ...state,
        tickets: [newT, ...state.tickets],
        mailbox: state.mailbox.map(m => m.id === action.emailId ? { ...m, ticketId: nextId } : m),
        selectedId: nextId,
        view: "inbox",
        mailboxOpen: false,
      };
    }
    case "createArticle": {
      // Optimistic: prepend with a temp negative ID so the API's IDENTITY id can
      // come in via "articleCreated" without colliding.
      const tempId = -(Date.now());
      const newA = {
        id: tempId,
        title: action.title,
        category: action.category,
        tags: action.tags,
        author: ME_ID,
        updated: 0,
        summary: action.summary,
        body: action.body,
        status: action.status || 'draft',
      };
      return { ...state, kbArticles: [newA, ...state.kbArticles], newArticleOpen: false, view: "kb", kbOpenId: tempId };
    }
    case "articleCreated": {
      // Reconcile the optimistic temp-id article with the API's real row.
      // Match by title (the optimistic row carries the same title the user typed).
      const local = action.localId != null
        ? state.kbArticles.find(a => a.id === action.localId)
        : state.kbArticles.find(a => !a.updatedAt && a.title === action.article.title);
      const filtered = local
        ? state.kbArticles.filter(a => a.id !== local.id)
        : state.kbArticles;
      if (filtered.some(a => a.id === action.article.id)) {
        return {
          ...state,
          kbArticles: filtered,
          kbOpenId: state.kbOpenId === local?.id ? action.article.id : state.kbOpenId,
        };
      }
      return {
        ...state,
        kbArticles: [action.article, ...filtered],
        kbOpenId: state.kbOpenId === local?.id ? action.article.id : state.kbOpenId,
      };
    }
    case "updateArticle":
      return {
        ...state,
        kbArticles: state.kbArticles.map(a => a.id === action.id ? { ...a, ...action.updates, updated: 0 } : a),
      };
    case "hydrateArticle":
      // Local-only merge - KBArticleView fires this after fetching an
      // article's body via GET /api/kb/{id} (the initial /kb list is slim
      // and doesn't include bodies). Intentionally NOT in syncAction so
      // no PATCH request is generated - this is a pure state hydration.
      return {
        ...state,
        kbArticles: state.kbArticles.map(a => a.id === action.id ? { ...a, ...action.article } : a),
      };
    case "deleteArticle":
      return {
        ...state,
        kbArticles: state.kbArticles.filter(a => a.id !== action.id),
        kbOpenId: state.kbOpenId === action.id ? null : state.kbOpenId,
        view: state.kbOpenId === action.id ? 'kb' : state.view,
      };
    case "toggleFavourite":
      // Optimistic flip - the API call confirms or quietly reverts on error
      return {
        ...state,
        kbArticles: state.kbArticles.map(a =>
          a.id === action.id ? { ...a, isFavourited: !a.isFavourited } : a
        ),
      };
    case "openEditArticle":
      return { ...state, editingArticleId: action.id };
    case "closeEditArticle":
      return { ...state, editingArticleId: null };
    case "openNewTicket":
      return { ...state, newTicketOpen: true };
    case "closeNewTicket":
      return { ...state, newTicketOpen: false };
    case "createTicket": {
      const nextId = -Date.now();
      const newT = {
        id: nextId,
        subject: action.subject,
        status: "new",
        priority: action.priority || "medium",
        category: action.category,
        subcat: action.subcat,
        location: action.location || "",
        requester: action.requester,
        assignee: action.assignee || null,
        created: 0, updated: 0,
        messages: [{ from: action.requester, body: action.body, time: 0, internal: false }],
        solution: "",
        tags: [],
      };
      return { ...state, tickets: [newT, ...state.tickets], newTicketOpen: false, selectedId: nextId, view: "inbox" };
    }
    case "ticketCreated": {
      // Reconcile optimistic local create with the real server-assigned ticket.
      const filtered = action.localId != null
        ? state.tickets.filter(t => t.id !== action.localId)
        : state.tickets;
      // If a background poll already brought the server ticket in, just drop the optimistic one.
      if (filtered.some(t => t.id === action.ticket.id)) {
        return {
          ...state,
          tickets: filtered,
          selectedId: state.selectedId === action.localId ? action.ticket.id : state.selectedId,
        };
      }
      return {
        ...state,
        tickets: [action.ticket, ...filtered],
        selectedId: state.selectedId === action.localId ? action.ticket.id : state.selectedId,
      };
    }
    // (mergeTickets was the background-poll reconciler for the old
    // "fetch every ticket then diff" model. Under pagination the poll
    // just refetches the current page via ticketsPageLoaded, so the
    // full-list diffing this case did is no longer meaningful - removed.)
    default:
      return state;
  }
}

// ─── URL routing (path-based, History API) ───────────────────────────────────
// Maps state.view ↔ URL path so views are bookmarkable / shareable / refresh-safe.
// Path-based (not hash) - relies on SWA's 404→/index.html rewrite in
// staticwebapp.config.json. Deep links: /tickets/42 opens ticket 42; /kb/3
// opens KB article 3. Old #/all bookmarks are migrated on load (see below).
const ROUTES = {
  // path             → state.view value      (canonical path first per view)
  '/home':            'home',
  '/':                'home',          // bare root resolves to home
  '/all-tickets':     'all',
  '/all-open':        'inbox',
  '/my-tickets':      'mine',
  '/unassigned':      'unassigned',
  '/resolved':        'resolved',
  '/fa-ib':           'fa-ib',
  '/recycle-bin':     'trash',
  '/kb':              'kb',
  '/dashboard':       'stats',
  '/tasks-board':     'tasks',
  '/tasks':           'tasks-mine',
  '/tasks-recycle':   'tasks-trash',
  '/contacts':        'contacts',
  '/evolution-matters': 'evolution-matters',
  '/apps':            'claude-apps',
  '/feedback':        'feedback-requests',
  '/settings':        'settings',
  '/user-requests':   'user-requests',
  '/wsm-policies':    'wsm',
  '/access-passes':   'tap',
  '/duo-passes':      'duo-passes',
  '/wallboard':       'wallboard',
  '/rota':            'rota',
};

// Old paths from the pre-switch hash-routing era. On initial load we redirect
// these so existing bookmarks/emails keep working. Keep this list around
// indefinitely - bookmarks are forever.
const LEGACY_PATH_REDIRECTS = {
  '/all':      '/all-tickets',
  '/closed':   '/resolved',
  '/recycle':  '/recycle-bin',
  // '/requests' USED to redirect to '/feedback' back when the internal
  // Platform Requests page owned that URL. That URL is now the user-facing
  // portal (chromeless, non-IT accessible). If anyone still has a
  // /requests bookmark meaning the feedback tracker, they can use
  // /feedback directly (the label in the sidebar is now "Feedback" too).
};

// Reverse lookup view → preferred path (first match wins). Order in ROUTES
// matters: '/home' is listed before '/' so 'home' resolves to '/home'.
const VIEW_TO_PATH = (() => {
  const out = {};
  for (const [path, view] of Object.entries(ROUTES)) if (!(view in out)) out[view] = path;
  return out;
})();

const parseRoute = (pathname) => {
  const p = pathname || '/';
  if (p === '/' || p === '') return { view: 'home', selectedId: null, kbOpenId: null };
  const tm = p.match(/^\/tickets\/(\d+)$/);
  if (tm) return { view: 'inbox', selectedId: parseInt(tm[1], 10), kbOpenId: null };
  const km = p.match(/^\/kb\/(\d+)$/);
  if (km) return { view: 'kb', selectedId: null, kbOpenId: parseInt(km[1], 10) };
  if (ROUTES[p]) return { view: ROUTES[p], selectedId: null, kbOpenId: null };
  return null;
};

const stateToPath = (state) => {
  if (state.selectedId) return `/tickets/${state.selectedId}`;
  if (state.view === 'kb' && state.kbOpenId) return `/kb/${state.kbOpenId}`;
  return VIEW_TO_PATH[state.view] || '/home';
};

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "accent": "#5B6CFF",
  "density": "comfortable",
  "showPriority": true,
  "sidebarCount": true,
  "theme": "light"
}/*EDITMODE-END*/;

// True on phone-sized screens. Reacts to rotation/resize. Used ONLY to swap the
// whole IT app for the stripped phone shell below - desktop/tablet render path
// is untouched. 600px keeps tablets (iPad 768+) on the full app.
function useIsPhone() {
  const q = '(max-width: 600px)';
  const [isPhone, setIsPhone] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches,
  );
  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return isPhone;
}

// Phone experience: Access Passes only, no sidebar, nothing else. Rendered from
// Root in place of <App> for IT users on a phone. Auth + IT-group gating already
// happened in Root, so this only ever shows to a signed-in IT user. Non-issuers
// still get server 403s inside TapView (surfaced as a message).
function PhoneApp() {
  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <header style={{
        flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 14px', paddingTop: 'calc(12px + env(safe-area-inset-top))',
        borderBottom: '1px solid var(--line)',
        background: 'var(--bg-elev)', color: 'var(--text)',
      }}>
        <Icon name="key" size={16}/>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Access Passes</span>
      </header>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        <TapView mobile/>
      </div>
    </div>
  );
}

// Phone-only bottom tab bar - replaces the slide-out sidebar on phones (desktop
// keeps its sidebar). "More" opens a launcher list of every other section.
const BottomNav = ({ state, dispatch }) => {
  const TICKET_VIEWS = ['inbox', 'all', 'mine', 'unassigned', 'resolved', 'trash'];
  const TASK_VIEWS   = ['tasks', 'tasks-mine', 'tasks-trash'];
  const MORE_VIEWS   = ['more', 'contacts', 'stats', 'rota', 'tap', 'duo-passes', 'user-requests', 'wsm', 'feedback-requests', 'settings'];
  const tabs = [
    { view: 'home',       icon: 'home',     label: 'Home',    on: state.view === 'home' },
    { view: 'inbox',      icon: 'inbox',    label: 'Tickets', on: TICKET_VIEWS.includes(state.view) },
    { view: 'tasks-mine', icon: 'check',    label: 'Tasks',   on: TASK_VIEWS.includes(state.view) },
    { view: 'kb',         icon: 'openBook', label: 'KB',      on: state.view === 'kb' },
    { view: 'more',       icon: 'dots',     label: 'More',    on: MORE_VIEWS.includes(state.view) },
  ];
  return (
    <nav className="mobile-tabbar">
      {tabs.map(t => (
        <button key={t.view} className={`mtab${t.on ? ' on' : ''}`} onClick={() => dispatch({ type: 'navigate', view: t.view })}>
          <Icon name={t.icon} size={21}/>
          <span>{t.label}</span>
        </button>
      ))}
    </nav>
  );
};

// The "More" tab: a simple full-page list of every section not on the bottom bar.
const MobileMoreMenu = ({ dispatch }) => {
  const items = [
    { view: 'all',               icon: 'inbox',         label: 'All tickets' },
    { view: 'unassigned',        icon: 'flag',          label: 'Unassigned' },
    { view: 'tasks',             icon: 'check',         label: 'Team tasks' },
    { view: 'stats',             icon: 'bar',           label: 'Dashboard' },
    { view: 'rota',              icon: 'calendar',      label: 'IT Rota' },
    { view: 'contacts',          icon: 'users',         label: 'Supplier Contacts' },
    { view: 'evolution-matters', icon: 'book',          label: 'Evolution Matters' },
    { view: 'tap',               icon: 'key',           label: 'Access Passes' },
    { view: 'duo-passes',        icon: 'bookmark',      label: 'Duo Passes' },
    { view: 'user-requests',     icon: 'archive',       label: 'User Requests' },
    { view: 'wsm',               icon: 'book',          label: 'WSM Policies' },
    { view: 'feedback-requests', icon: 'messageSquare', label: 'Feedback' },
    { view: 'settings',          icon: 'settings',      label: 'Settings' },
    // Claude Apps (Firm): one link to the launcher page (kept last).
    { view: 'claude-apps',       icon: 'grip',          label: 'Claude Apps (Firm)' },
  ];
  const rowStyle = { width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '15px 18px', background: 'none',
    border: 'none', borderBottom: '1px solid var(--line)', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: 'var(--text)' };
  return (
    <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'var(--bg)', padding: '8px 0 16px' }}>
      {items.map(it => {
        const key = it.view || it.href || it.label;
        const inner = (
          <>
            <span style={{ display: 'flex', width: 22, justifyContent: 'center', color: 'var(--text-subtle)' }}><Icon name={it.icon} size={18}/></span>
            <span style={{ flex: 1, fontSize: 15, fontWeight: 500 }}>{it.label}</span>
          </>
        );
        // External link → open in a new tab.
        if ('href' in it) {
          if (!it.href) return (
            <div key={key} style={{ ...rowStyle, cursor: 'default', opacity: 0.5 }} title="Link to be confirmed">
              {inner}<span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Soon</span>
            </div>
          );
          return (
            <a key={key} href={it.href} target="_blank" rel="noopener noreferrer" style={{ ...rowStyle, textDecoration: 'none' }}>
              {inner}<Icon name="link" size={14}/>
            </a>
          );
        }
        // Internal view → navigate.
        return (
          <button key={key} onClick={() => dispatch({ type: 'navigate', view: it.view })} style={rowStyle}>
            {inner}<Icon name="chevronRight" size={14}/>
          </button>
        );
      })}
    </div>
  );
};

function App({ currentUser, staffList, teamVersion }) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const isPhone = useIsPhone();
  // Persist the View ▸ column layout (see restoreColumnConfig above).
  React.useEffect(() => {
    try { localStorage.setItem(COLUMN_CONFIG_KEY, JSON.stringify({ tableView: state.tableView, columns: state.columns })); }
    catch (_) { /* quota / private mode */ }
  }, [state.tableView, state.columns]);
  // Persist the per-view sticky status filter (see withStickyStatus above).
  // Keyed on the query's own view so it stays correct even if state.view
  // and ticketsQuery.view momentarily differ during a navigation.
  React.useEffect(() => {
    const qv = state.ticketsQuery.view;
    if (TICKET_LIST_VIEWS.has(qv)) writeStickyStatus(qv, state.ticketsQuery.status);
  }, [state.ticketsQuery.view, state.ticketsQuery.status]);
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  // Persist appearance tweaks to localStorage - per-browser rather than
  // per-user-in-DB (which is what most people want for cosmetic settings:
  // your accent colour saved on your laptop, not travelling to Rob's too).
  // Restore on mount; write on every change. Wrapped in try/catch because
  // Safari private mode throws on setItem when quota is 0.
  const APPEARANCE_KEY = 'triage:appearance-v1';
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(APPEARANCE_KEY);
      if (raw) setTweak(JSON.parse(raw));
    } catch (_) { /* private mode or corrupt JSON - ignore */ }
    // Only fires on mount; ignore setTweak dep-check as it's stable.

  }, []);
  React.useEffect(() => {
    try { localStorage.setItem(APPEARANCE_KEY, JSON.stringify(t)); }
    catch (_) { /* quota / private mode - non-fatal */ }
  }, [t]);
  const [apiReady, setApiReady] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  // Debounce the search box into ticketsQuery.q so we don't fire a fetch on
  // every keystroke - 300ms is the sweet spot where it feels immediate but
  // typing quickly doesn't spam the API.
  //
  // No-op guard: this effect also runs on MOUNT (searchQuery starts ''),
  // and setTicketsQuery unconditionally sets ticketsMeta.loading=true.
  // Because q hadn't actually changed, querySignature stayed the same,
  // the refetch effect never fired, and nothing cleared loading until
  // the 20s poll - pagination buttons sat disabled and empty states were
  // suppressed for up to 20s after page load. Skip when q is unchanged.
  React.useEffect(() => {
    const t = setTimeout(() => {
      if ((stateRef.current?.ticketsQuery?.q || '') === searchQuery) return;
      dispatch({ type: 'setTicketsQuery', patch: { q: searchQuery } });
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);
  // Mobile-only sidebar toggle. Untouched on desktop because the CSS for
  // .sidebar-open only kicks in inside the @media (max-width: 768px) block;
  // toggling this on a desktop session has no visible effect.
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Categories - load from API and mirror into the global CATEGORIES + SUBCATS.
  // In-place mutation (length = 0; push(...)) / key wipe keeps every existing
  // reference (kb.jsx, stats.jsx, tickets.jsx, NewTicketModal, etc.) working
  // without prop-drilling. Response shape includes each category's
  // subcategories as [{ id, label, sortOrder }] - flattened into the
  // legacy SUBCATS { CATEGORY_ID: [label, label, ...] } shape here so
  // downstream consumers (which expect string arrays) are unaffected.
  const reloadCategories = React.useCallback(() => {
    return api("GET", "/categories")
      .then(cats => {
        if (Array.isArray(cats) && cats.length > 0) {
          CATEGORIES.length = 0;
          cats.forEach(c => CATEGORIES.push(c));
          // Rebuild SUBCATS in place: clear existing keys, then repopulate
          // from the embedded subcategories array on each category. Keys
          // for categories with no subcategories are removed too so a
          // deleted-then-recreated category doesn't inherit stale rows.
          for (const k of Object.keys(SUBCATS)) delete SUBCATS[k];
          for (const c of cats) {
            if (Array.isArray(c.subcategories) && c.subcategories.length) {
              // Alphabetical - matches the settings screen ordering + how
              // the API returns them. No manual sort field surfaced anywhere.
              SUBCATS[c.id] = c.subcategories
                .map(s => s.label)
                .sort((a, b) => a.localeCompare(b));
            }
          }
          dispatch({ type: "set", categories: cats });
        }
      })
      .catch(err => console.warn("[categories] load failed:", err.message));
  }, []);
  // First load on mount. Previously gated on apiReady (set only after the
  // tickets fetch completed), which serialized /categories behind the
  // tickets round-trip for no reason - App only mounts once the user is
  // signed in, so the token is already available. Firing on mount takes
  // it off the critical path.
  React.useEffect(() => { reloadCategories(); }, [reloadCategories]);

  // Build the querystring the paginated /api/tickets endpoint expects from
  // the current ticketsQuery + page. Kept as a pure function so it can be
  // reused for both initial fetch and Load More.
  const buildTicketsQS = React.useCallback((query, page, size) => {
    const params = new URLSearchParams();
    params.set('view',  query.view || 'all');
    params.set('page',  String(page));
    params.set('size',  String(size));
    params.set('sort',  query.sort?.col || 'requestTime');
    params.set('dir',   query.sort?.dir || 'desc');
    if (query.view === 'mine' && ME_ID) params.set('me', ME_ID);
    if (query.assignee)                 params.set('assignee',  query.assignee);
    if (query.status)                   params.set('status',    query.status);
    if (query.category)                 params.set('category',  query.category);
    if (query.requester)                params.set('requester', query.requester);
    if (query.q && query.q.trim())      params.set('q',        query.q.trim());
    return params.toString();
  }, []);

  // Fetches a page of tickets. `append=false` (default) replaces the current
  // page; `append=true` is the Load More flow that concatenates. Also
  // rehydrates the mailbox "Recently Converted" list from any email-sourced
  // tickets on the page - same behaviour the old reloadTickets had.
  const fetchTicketsPage = React.useCallback(async ({ append = false, sync = false, page = 1 } = {}) => {
    setRefreshing(true);
    try {
      if (sync) {
        await api('POST', '/mailbox/sync').catch(e => console.warn('[API] Sync failed:', e.message));
      }
      const q = stateRef.current.ticketsQuery;
      const size = stateRef.current.ticketsMeta.size;
      const qs = buildTicketsQS(q, page, size);
      const resp = await api('GET', `/tickets?${qs}`);
      if (!resp || !Array.isArray(resp.rows)) return;
      // Stale-response guard: if the query changed while this request was
      // in flight (rapid view switching, filter change racing the poll),
      // drop the response instead of clobbering state.tickets with rows
      // from the WRONG view - the losing response could land last and
      // stick until the next 20s poll. Comparing the querystring we sent
      // against one built from the CURRENT query catches every axis
      // (view, filters, sort, search) in one check.
      const qsNow = buildTicketsQS(stateRef.current.ticketsQuery, page, stateRef.current.ticketsMeta.size);
      if (qsNow !== qs) {
        console.log('[tickets] dropping stale response for superseded query');
        return;
      }
      dispatch({
        type: append ? 'ticketsPageAppended' : 'ticketsPageLoaded',
        rows: resp.rows,
        total: resp.total,
        page:  resp.page,
        size:  resp.size,
        hasMore: resp.hasMore,
      });
      // Mirror the mailbox "Recently Converted" indicator off any tickets on
      // the current page that came from an email - same shape the old
      // full-list dispatch produced.
      const converted = resp.rows
        .filter(t => t.sourceEmailId)
        .map(t => ({ id: t.sourceEmailId, from: t.requester || t.requesterName || '', subject: t.subject, body: '', received: t.created, ticketId: t.id }));
      if (converted.length) dispatch({ type: 'set', mailbox: converted });
    } catch (err) {
      console.warn('[API] Tickets fetch failed:', err.message);
    } finally {
      setRefreshing(false);
    }
  }, [buildTicketsQS]);

  // Sidebar badges. Cheap COUNT(*) round-trip; called on mount and after
  // any ticket mutation so the numbers stay honest.
  const fetchCounts = React.useCallback(async () => {
    try {
      const qs = ME_ID ? `?me=${encodeURIComponent(ME_ID)}` : '';
      const counts = await api('GET', `/tickets/counts${qs}`);
      if (counts && typeof counts === 'object') dispatch({ type: 'setCounts', counts });
    } catch (err) {
      console.warn('[API] Counts fetch failed:', err.message);
    }
  }, []);

  // Backwards-compatible shim - existing callers (refresh button, task
  // completion event, etc.) just want "reload what's showing". Now they
  // fetch the current page and the counts in parallel.
  const reloadTickets = React.useCallback((triggerSync = false) => {
    Promise.all([
      fetchTicketsPage({ append: false, sync: triggerSync, page: 1 }),
      fetchCounts(),
    ]);
  }, [fetchTicketsPage, fetchCounts]);

  // Keep a ref to latest state so syncAction always sees current selected set etc.
  const stateRef = React.useRef(state);
  React.useEffect(() => { stateRef.current = state; }, [state]);

  // ── URL → State (initial load + browser back/forward + manual edits) ────────
  React.useEffect(() => {
    // Migrate pre-switch URL formats. Two cases:
    //   1. Old hash-style bookmarks like /#/all → strip the hash, redirect
    //      to the new path (and possibly rename via LEGACY_PATH_REDIRECTS).
    //   2. Old path-style names like /all (post-hash-migration but pre-rename)
    //      → redirect to the new name.
    // replaceState (not pushState) so back-button doesn't return to the old URL.
    const oldHash = window.location.hash;
    if (oldHash && oldHash.startsWith('#/')) {
      const oldPath = oldHash.slice(1);
      const newPath = LEGACY_PATH_REDIRECTS[oldPath] || oldPath;
      window.history.replaceState(null, '', newPath);
    } else if (LEGACY_PATH_REDIRECTS[window.location.pathname]) {
      window.history.replaceState(null, '', LEGACY_PATH_REDIRECTS[window.location.pathname]);
    }

    const sync = () => {
      const route = parseRoute(window.location.pathname);
      if (!route) return;
      const cur = stateRef.current;
      // Only dispatch if something actually differs - avoids redundant renders
      if (cur.view !== route.view || cur.selectedId !== route.selectedId || cur.kbOpenId !== route.kbOpenId) {
        // Also sync ticketsQuery.view so the server-side fetch matches the
        // URL - otherwise landing on /all keeps ticketsQuery.view at its
        // initial 'inbox' and the wrong page loads. TICKET_LIST_VIEWS
        // filters to only the views the tickets endpoint understands.
        const patch = { view: route.view, selectedId: route.selectedId, kbOpenId: route.kbOpenId, selected: new Set() };
        if (TICKET_LIST_VIEWS.has(route.view) && cur.ticketsQuery.view !== route.view) {
          // Same per-view sticky-status restore as the navigate reducer, so a
          // reload or back/forward onto a view behaves identically to
          // clicking it in the sidebar. (openOnly/resolved clamps are applied
          // by the reducer path; on first load the landing view is a fresh
          // read, and an invalid saved status self-corrects on next nav.)
          const restored = withStickyStatus({ ...cur.ticketsQuery, view: route.view }, route.view);
          const openOnly = route.view === 'inbox' || route.view === 'unassigned';
          if (openOnly && restored.status === 'resolved') restored.status = null;
          if (route.view === 'resolved' && restored.status && restored.status !== 'resolved') restored.status = null;
          patch.ticketsQuery = restored;
          patch.ticketsMeta  = { ...cur.ticketsMeta, page: 1, loading: true };
        }
        dispatch({ type: 'set', ...patch });
      }
    };
    sync();
    // popstate fires on browser back/forward. pushState (programmatic nav)
    // doesn't fire it, but we sync via the State→URL effect below, so all
    // navigation paths converge.
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  // ── State → URL (user navigation, ticket selection, KB drill-in) ────────────
  React.useEffect(() => {
    const target = stateToPath(state);
    if (window.location.pathname !== target) {
      window.history.pushState(null, '', target);
    }
  }, [state.view, state.selectedId, state.kbOpenId]);

  // Listen for events that need a fresh tickets fetch. Currently fired by
  // tasks.jsx after a task with convertToTicketOnComplete is completed -
  // the server auto-creates a closed ticket, so the tickets list is now
  // stale until we reload it.
  React.useEffect(() => {
    const handler = () => reloadTickets();
    window.addEventListener('triage:tickets-need-refresh', handler);
    return () => window.removeEventListener('triage:tickets-need-refresh', handler);
  }, [reloadTickets]);

  // Counter of in-flight API writes. Background poll skips while > 0 so an in-flight
  // write can't be clobbered by a stale GET response.
  const pendingWritesRef = React.useRef(0);

  // Wrapped dispatch: update local state immediately (optimistic), then persist to API.
  const apiDispatch = React.useCallback((action) => {
    dispatch(action);
    const promise = syncAction(action, stateRef.current);
    if (promise) {
      pendingWritesRef.current++;
      promise
        .then(result => {
          if (action.type === "mergeDuplicates" && result) {
            dispatch({ type: "mergedTicketLoaded", ticket: result });
          }
          if (action.type === "splitMessage" && result) {
            dispatch({ type: "splitMessageLoaded", original: result.original, newTicket: result.newTicket });
          }
          if (action.type === "convertEmailToTicket" && result) {
            dispatch({ type: "emailTicketCreated", emailId: action.emailId, localId: action.localId, ticket: result });
          }
          if (action.type === "createTicket" && result) {
            // Find the optimistic local we just created so we can swap it for the server's real ticket
            const local = stateRef.current.tickets.find(
              t => !t.updatedAt && t.subject === action.subject && t.requester === action.requester
            );
            dispatch({ type: "ticketCreated", localId: local ? local.id : null, ticket: result });
          }
          if (action.type === "createArticle" && result) {
            const local = stateRef.current.kbArticles.find(
              a => a.id < 0 && a.title === action.title
            );
            dispatch({ type: "articleCreated", localId: local ? local.id : null, article: result });
          }
        })
        .catch(err => console.warn("[API sync]", action.type, err.message))
        .finally(() => {
          pendingWritesRef.current--;
          // After a mutation that touches tickets, refresh both:
          //   1. Sidebar counts - server-side COUNT(*)s, otherwise badges
          //      lie until the next 20s poll.
          //   2. Current tickets page - if the mutation changed the
          //      ticket's view membership (e.g. assigning a ticket on the
          //      Unassigned view, or resolving one on the All-open view),
          //      the row needs to visually disappear. Optimistic reducer
          //      updates the row in place; only a refetch removes it.
          // Skip for non-ticket actions so KB / task / settings mutations
          // don't trigger a wasted tickets refetch.
          const t = action.type;
          const touchesTickets = (
            t === 'assign' || t === 'bulkAssign' ||
            t === 'setStatus' || t === 'bulkStatus' ||
            t === 'setPriority' || t === 'renameTicket' ||
            t === 'setCategory' || t === 'setSubcat' ||
            t === 'setLocation' || t === 'setRequester' ||
            t === 'resolve' || t === 'assignAndResolve' ||
            t === 'saveSolution' || t === 'addMessage' || t === 'editNote' || t === 'deleteNote' ||
            t === 'convertEmailToTicket' || t === 'createTicket' ||
            t === 'trashTicket' || t === 'trashTickets' ||
            t === 'restoreTicket' || t === 'restoreTickets' || t === 'emptyTrash' ||
            t === 'mergeDuplicates' || t === 'splitMessage'
          );
          if (touchesTickets) {
            fetchCounts();
            // Skip the tickets refetch when the user has a ticket open -
            // the mutation already updated the row in state via the
            // optimistic reducer, and a refetch would slide the list out
            // from under them (they're not looking at it anyway).
            if (!stateRef.current.selectedId) {
              fetchTicketsPage({ append: false, page: stateRef.current.ticketsMeta.page || 1 });
            }
          }
        });
    }
  }, [fetchCounts, fetchTicketsPage]);

  // Background auto-refresh.
  //   - Every 20s: refetch the current page + sidebar counts. Under
  //     pagination this is dramatically cheaper than the old "fetch every
  //     ticket in the DB" - just the ~50 rows currently on screen.
  //   - Every 6th tick (= 2 minutes): also POST /mailbox/sync first so we
  //     don't have to wait for the server's separate `pollMailbox` cron to
  //     fire. Means new inbound emails surface within ~2 min of arrival
  //     even if the server cron is slow / cold / skipped.
  // Pauses while tab is hidden and while local writes are in flight.
  React.useEffect(() => {
    if (!apiReady) return;
    let cancelled = false;
    let tickCount = 0;
    const tick = async () => {
      if (cancelled || document.hidden || pendingWritesRef.current > 0) return;
      tickCount++;
      const shouldSync = (tickCount % 6) === 0;
      try {
        // Poll uses fetchTicketsPage/fetchCounts - same code path as the
        // manual refresh button, so behaviour stays consistent whether the
        // trigger is the timer or a user click. Always refetches page 1
        // (rather than the user's current page) so a background poll never
        // hides earlier rows out from under them. Trade-off: if they'd
        // clicked Load More the appended pages are dropped and they'd need
        // to click again. Acceptable at this size; can revisit later.
        await fetchTicketsPage({ append: false, sync: shouldSync, page: 1 });
        if (cancelled) return;
        await fetchCounts();
      } catch (_) { /* silent - next interval retries */ }
    };
    const interval = setInterval(tick, 20000);
    // Re-poll immediately when the user returns to the tab.
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [apiReady]);

  // Load KB articles from API on mount (parallel with tickets). Uses ?slim=1
  // so the response drops the (often HTML, sometimes hundreds of KB) body
  // column - the home tiles and KB list only need metadata. When a user
  // opens an article, KBArticleView hydrates the body from GET /api/kb/{id}.
  // Net effect: initial page load stopped waiting on ~450KB of article
  // bodies most users never actually read.
  React.useEffect(() => {
    api("GET", "/kb?slim=1")
      .then(articles => {
        if (Array.isArray(articles)) dispatch({ type: "set", kbArticles: articles });
      })
      .catch(err => console.warn("[API] Could not load KB articles:", err.message));
  }, []);

  // Initial load - page 1 of tickets + sidebar counts. Runs once on mount.
  // Subsequent refetches are driven by the ticketsQuery effect below
  // (which fires on any filter/sort/search change) and by fetchCounts +
  // fetchTicketsPage calls from mutations.
  React.useEffect(() => {
    Promise.all([
      fetchTicketsPage({ append: false, page: 1 }),
      fetchCounts(),
    ]).finally(() => setApiReady(true));
  }, []);

  // Refetch tickets whenever the query changes. Serialise the query so this
  // fires on real changes (not on every render even when references churn).
  const querySignature = JSON.stringify(state.ticketsQuery);
  const firstQueryRun = React.useRef(true);
  React.useEffect(() => {
    if (firstQueryRun.current) {
      // Skip the very first mount - the initial-load effect above already
      // fetched page 1 with these defaults. Otherwise we'd double-fetch.
      firstQueryRun.current = false;
      return;
    }
    fetchTicketsPage({ append: false, page: 1 });
  }, [querySignature]);

  // Apply tweaks to root
  React.useEffect(() => {
    document.documentElement.style.setProperty("--accent", t.accent);
    document.documentElement.classList.toggle("density-compact", t.density === "compact");
  }, [t.accent, t.density]);

  // Apply theme (light / dark / auto). `auto` follows the OS-level
  // prefers-color-scheme media query and re-applies when the user flips
  // their system theme without a reload.
  React.useEffect(() => {
    const mode = t.theme || 'light';
    const applyTheme = () => {
      let effective = mode;
      if (mode === 'auto') {
        effective = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      document.documentElement.setAttribute('data-theme', effective);
      // Keep the mobile browser-chrome tint (theme-color) in step with the
      // app background, so iOS Safari's bars blend with the app rather than
      // framing it in grey. Read the computed body bg so it matches whatever
      // --bg the effective theme resolves to (light or dark).
      try {
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', getComputedStyle(document.body).backgroundColor);
      } catch { /* no DOM / ancient browser - safe to skip */ }
    };
    applyTheme();
    if (mode === 'auto' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = () => applyTheme();
      // addEventListener replaces the deprecated addListener; Safari <14
      // still needs the old API but we're targeting modern browsers only.
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
  }, [t.theme]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const onKey = (e) => {
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      const isEditable = tag === "input" || tag === "textarea" || !!document.activeElement?.isContentEditable;
      if (isEditable) return;
      const hasSel = !!window.getSelection()?.toString();
      if (e.key === "c" && !e.metaKey && !e.ctrlKey && !hasSel) { dispatch({type:"openNewTicket"}); }
      if (e.key === "g" && !e.metaKey && !e.ctrlKey && !hasSel) {
        const onSecond = (e2) => {
          if (e2.metaKey || e2.ctrlKey) return; // ignore Ctrl+R, Cmd+M etc.
          if (e2.key === "i") dispatch({type:"navigate", view:"inbox"});
          if (e2.key === "m") dispatch({type:"navigate", view:"mine"});
          if (e2.key === "u") dispatch({type:"navigate", view:"unassigned"});
          if (e2.key === "r") dispatch({type:"navigate", view:"resolved"});
          if (e2.key === "k") dispatch({type:"navigate", view:"kb"});
          if (e2.key === "s") dispatch({type:"navigate", view:"stats"});
        };
        window.addEventListener("keydown", onSecond, {once: true});
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Wallboard takes the full viewport - no sidebar, no topbar. Renders
  // directly so the rest of the app shell never mounts on this route.
  // Esc returns to home (handled inside WallboardView itself).
  if (state.view === 'wallboard') {
    return <WallboardView state={state} dispatch={apiDispatch}/>;
  }

  // The active screen. Extracted so the phone layout can wrap it in a flex box
  // (keeps height:100% views sized correctly above the bottom tab bar) while
  // desktop renders it directly, unchanged.
  const mainContent = (
    state.view === "home" ? (
      <HomeView state={state} dispatch={apiDispatch} currentUser={currentUser}/>
    ) : state.view === "settings" ? (
      <SettingsView state={state} dispatch={apiDispatch} reloadCategories={reloadCategories} staffList={staffList} tweaks={t} setTweak={setTweak} currentUser={currentUser}/>
    ) : state.view === "kb" ? (
      <KBView state={state} dispatch={apiDispatch} currentUser={currentUser}/>
    ) : state.view === "stats" ? (
      <StatsView state={state}/>
    ) : state.view === "tasks" ? (
      <TasksView/>
    ) : state.view === "tasks-mine" ? (
      <TasksView myTasksOnly={true}/>
    ) : state.view === "tasks-trash" ? (
      <TasksView trashView={true}/>
    ) : state.view === "contacts" ? (
      <ContactsView/>
    ) : state.view === "evolution-matters" ? (
      <EvolutionMattersView/>
    ) : state.view === "claude-apps" ? (
      <FirmAppsView/>
    ) : state.view === "rota" ? (
      <RotaView currentUser={currentUser}/>
    ) : state.view === "feedback-requests" ? (
      <FeedbackView/>
    ) : state.view === "user-requests" ? (
      <UserRequestsAdmin/>
    ) : state.view === "wsm" ? (
      <WsmPoliciesView/>
    ) : state.view === "tap" ? (
      <TapView/>
    ) : state.view === "duo-passes" ? (
      <DuoPassesView/>
    ) : state.view === "more" ? (
      <MobileMoreMenu dispatch={apiDispatch}/>
    ) : (
      <TicketsView state={state} dispatch={apiDispatch} onGoToPage={(page) => {
        dispatch({ type: 'ticketsLoading', loading: true });
        fetchTicketsPage({ append: false, page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}/>
    )
  );

  return (
    <div className={`app ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <Sidebar state={state} dispatch={apiDispatch} showCounts={t.sidebarCount} currentUser={currentUser} teamVersion={teamVersion} onNavigate={() => setSidebarOpen(false)}/>
      {/* Mobile-only backdrop - taps outside the open drawer close it.
          Hidden on desktop via CSS. */}
      <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)}/>
      <div className="main" style={{position:"relative"}}>
        <TopBar state={state} dispatch={apiDispatch} onRefresh={reloadTickets} refreshing={refreshing} searchQuery={searchQuery} onSearch={setSearchQuery} onToggleSidebar={() => setSidebarOpen(v => !v)}/>
        {!apiReady && (
          <div style={{position:"absolute", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", background:"var(--bg)", zIndex:10, fontSize:13, color:"var(--text-subtle)"}}>
            Loading tickets…
          </div>
        )}
        {isPhone ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{mainContent}</div>
        ) : mainContent}
        {state.selected.size > 0 && state.view !== "kb" && state.view !== "stats" && (
          <BulkBar state={state} dispatch={apiDispatch}/>
        )}
        {state.view === "trash" && state.selected.size === 0 && (state.counts?.trash || 0) > 0 && (
          <div className="sel-bar" style={{pointerEvents:"auto"}}>
            <span style={{color:"rgba(255,255,255,0.7)"}}>{state.counts.trash} item{state.counts.trash !== 1 ? "s" : ""} in recycle bin</span>
            <div className="sep"/>
            <button className="danger" onClick={() => { if (window.confirm("Permanently delete all items in the recycle bin?")) apiDispatch({type:"emptyTrash"}); }}>
              <Icon name="trash" size={11}/> Empty recycle bin
            </button>
          </div>
        )}
        {isPhone && <BottomNav state={state} dispatch={apiDispatch}/>}
      </div>

      {state.newTicketOpen && (
        <NewTicketModal dispatch={apiDispatch} staffList={staffList}/>
      )}

      {state.newArticleOpen && (
        <NewArticleModal dispatch={apiDispatch}/>
      )}
      {state.editingArticleId != null && (() => {
        const article = state.kbArticles.find(a => a.id === state.editingArticleId);
        return article ? <EditArticleModal dispatch={apiDispatch} article={article}/> : null;
      })()}

      {state.mailboxOpen && (
        <MailboxDrawer state={state} dispatch={apiDispatch} onSync={() => reloadTickets(true)}/>
      )}

      <TweaksPanel>
        <TweakSection label="Theme"/>
        <TweakColor
          label="Accent"
          value={t.accent}
          options={["#5B6CFF","#2D7AEA","#1F8A5B","#C24E63","#252525"]}
          onChange={(v) => setTweak("accent", v)}
        />
        <TweakSection label="Layout"/>
        <TweakRadio
          label="Density"
          value={t.density}
          options={["compact","comfortable"]}
          onChange={(v) => setTweak("density", v)}
        />
        <TweakToggle
          label="Show priority bars"
          value={t.showPriority}
          onChange={(v) => setTweak("showPriority", v)}
        />
        <TweakToggle
          label="Sidebar counts"
          value={t.sidebarCount}
          onChange={(v) => setTweak("sidebarCount", v)}
        />
      </TweaksPanel>
    </div>
  );
}

// ---- Sidebar ----
const Sidebar = ({ state, dispatch, showCounts, currentUser, teamVersion, onNavigate }) => {
  // Counts come from state.counts (populated by /api/tickets/counts on load
  // and after every ticket mutation). Previously computed by filtering the
  // in-memory tickets array; with pagination that array is only the current
  // page, so a client-side .filter would only ever see a slice of the truth.
  const counts = state.counts || { inbox: 0, mine: 0, unassigned: 0, resolved: 0, trash: 0, all: 0 };
  // Tap a nav item → navigate + (on mobile) close the drawer. onNavigate is
  // a no-op when the parent doesn't pass it, so this is safe everywhere.
  const handleNav = (view) => {
    dispatch({type:"navigate", view});
    if (onNavigate) onNavigate();
  };
  // Tickets sub-views collapse under "All tickets". Collapsed by default; the
  // choice sticks across reloads/navigation via localStorage.
  const [ticketsOpen, setTicketsOpen] = React.useState(() => {
    try { return localStorage.getItem('sb.ticketsOpen') === '1'; } catch (_) { return false; }
  });
  const toggleTickets = (e) => {
    e.stopPropagation();
    setTicketsOpen(o => {
      const next = !o;
      try { localStorage.setItem('sb.ticketsOpen', next ? '1' : '0'); } catch (_) { /* private mode */ }
      return next;
    });
  };
  const NavItem = ({ id, icon, label, count, indent }) => (
    <div className={`sb-item ${state.view === id ? "active" : ""}`} style={indent ? { paddingLeft: 28 } : undefined} onClick={() => handleNav(id)}>
      <Icon name={icon} className="ico"/>
      <span>{label}</span>
      {showCounts && count != null && count > 0 && <span className="count">{count}</span>}
    </div>
  );
  // External links to Claude-built firm apps that live on their own domains.
  // Rendered as an <a> so they open in a new tab (styled as sb-item to match the
  // rest of the nav; textDecoration:none strips the browser's anchor underline).
  // An item with no href yet renders disabled with a "soon" hint.
  const me = getUser(ME_ID);

  return (
    <div className="sidebar">
      <div
        className="sb-brand"
        style={{ cursor: 'pointer' }}
        onClick={() => dispatch({ type: 'navigate', view: 'home' })}
        title="Home"
      >
        {/* Triage primary lockup inlined as SVG so it can be recoloured for
            the dark sidebar (AGE is white, subtitle is muted white). The
            triage-logo-primary.svg file in $web is kept for email use.
            Clickable - navigates to the home dashboard. */}
        <svg viewBox="0 0 240 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Triage" className="sb-logo">
          <rect x="24" y="24" width="30" height="23" rx="5" fill="#0D9488" fillOpacity="0.35"/>
          <rect x="16" y="34" width="30" height="23" rx="5" fill="#0D9488"/>
          <line x1="22" y1="44" x2="42" y2="44" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
          <line x1="22" y1="50" x2="34" y2="50" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
          <text x="66" y="52" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif" fontSize="32" fontWeight="700" letterSpacing="2">
            <tspan fill="#5EEAD4">TRI</tspan>
            <tspan fill="#FFFFFF">AGE</tspan>
          </text>
          <text x="68" y="70" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif" fontSize="11" fontWeight="500" letterSpacing="3" fill="rgba(255,255,255,0.55)">IT Helpdesk</text>
        </svg>
      </div>

      {/* Both buttons center their content, but each label is wrapped in a
          fixed-width span so the (icon + label) block has the same width in
          both buttons. Centering an identical-width block in identical-width
          buttons puts the "+" at the same x-position in each - aligned
          across rows. minWidth must fit the longest label ("New ticket"). */}
      <button className="btn primary" style={{margin: "0 4px 6px", justifyContent:"center", gap: 8}} onClick={() => dispatch({type:"openNewTicket"})}>
        <Icon name="plus" size={11}/>
        <span style={{display:"inline-block", minWidth: 64, textAlign:"left"}}>New ticket</span>
      </button>
      <button
        className="btn primary"
        style={{margin: "0 4px 8px", justifyContent:"center", gap: 8}}
        onClick={() => {
          window._triagePendingNewTask = true;
          dispatch({type:"navigate", view: "tasks-mine"});
          if (onNavigate) onNavigate();   // close mobile drawer if open
        }}
      >
        <Icon name="plus" size={11}/>
        <span style={{display:"inline-block", minWidth: 64, textAlign:"left"}}>New task</span>
      </button>

      <div className="sb-nav">
      <NavItem id="home"       icon="home"    label="Home"/>

      <div className="sb-group">Tickets</div>
      {/* "All tickets" is the parent; the other views collapse underneath and
          the open/closed choice sticks (see ticketsOpen). Clicking the row
          navigates; the caret toggles the sub-views without navigating. */}
      <div className={`sb-item ${state.view === "all" ? "active" : ""}`} onClick={() => handleNav("all")}>
        <Icon name="inbox" className="ico"/>
        <span>All tickets</span>
        {showCounts && counts.all != null && counts.all > 0 && <span className="count">{counts.all}</span>}
        <button
          type="button"
          onClick={toggleTickets}
          title={ticketsOpen ? "Hide ticket views" : "Show ticket views"}
          aria-label={ticketsOpen ? "Hide ticket views" : "Show ticket views"}
          style={{ marginLeft: "auto", background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: "0 2px", display: "flex", alignItems: "center" }}
        >
          <span style={{ transform: ticketsOpen ? "rotate(90deg)" : "none", transition: "transform 120ms", display: "inline-block", fontSize: 10 }}>▸</span>
        </button>
      </div>
      {ticketsOpen && (
        <>
          <NavItem id="inbox"      icon="filter"  label="All open"    count={counts.inbox} indent/>
          <NavItem id="mine"       icon="user"    label="My tickets"  count={counts.mine} indent/>
          <NavItem id="unassigned" icon="flag"    label="Unassigned"  count={counts.unassigned} indent/>
          <NavItem id="resolved"   icon="archive" label="Closed"      count={counts.resolved} indent/>
          <NavItem id="fa-ib"      icon="filter"  label="All FA/IB"   count={counts.faib} indent/>
          <NavItem id="trash"      icon="trash"   label="Recycle bin" count={counts.trash} indent/>
        </>
      )}

      <div className="sb-group">Workspace</div>
      <NavItem id="stats"    icon="bar"      label="Dashboard"/>
      <NavItem id="rota"     icon="calendar" label="IT Rota"/>
      <NavItem id="kb"       icon="openBook" label="Knowledge base"/>
      <NavItem id="contacts" icon="users"    label="Supplier Contacts"/>
      <NavItem id="tasks"    icon="check"    label="To-Do List"/>
      <div
        className={`sb-item${state.view === "tasks-mine" ? " active" : ""}`}
        style={{paddingLeft: 30}}
        onClick={() => handleNav("tasks-mine")}
      >
        <Icon name="user" className="ico" size={12}/>
        <span style={{fontSize:12}}>My Tasks</span>
      </div>
      <div
        className={`sb-item${state.view === "tasks-trash" ? " active" : ""}`}
        style={{paddingLeft: 30}}
        onClick={() => handleNav("tasks-trash")}
      >
        <Icon name="trash" className="ico" size={12}/>
        <span style={{fontSize:12}}>Recycle bin</span>
      </div>

      <div className="sb-group">Feedback</div>
      <NavItem id="feedback-requests" icon="messageSquare" label="Feedback"/>

      {/* Settings - admin-only management of categories etc. Replaces the
          per-user Team list that used to live here; that was redundant with
          the assignee column filter in the tickets list. */}
      <div className="sb-group">Admin</div>
      {/* User Requests admin: queue of things submitted through the public
          /requests portal. Sits above Settings so it appears in the same
          zone as things IT actively works on. */}
      {/* Alphabetical by label, with Settings pinned last regardless. */}
      <NavItem id="tap" icon="key" label="Access Passes"/>
      <NavItem id="duo-passes" icon="bookmark" label="Duo Passes"/>
      <NavItem id="evolution-matters" icon="archive" label="Evolution Matters"/>
      <NavItem id="user-requests" icon="inbox" label="User Requests"/>
      <NavItem id="wsm" icon="book" label="WSM Policies"/>
      <NavItem id="settings" icon="settings" label="Settings"/>

      {/* Claude Apps (Firm) - one link to the launcher page of the firm's
          Claude-built apps (each opens in a new tab from there). */}
      <div className="sb-group">Claude Apps (Firm)</div>
      <NavItem id="claude-apps" icon="grip" label="Firm Apps"/>

      </div>{/* end sb-nav */}
      <div className="sb-footer">
        <Avatar user={USERS.find(u => u.id === ME_ID) || (currentUser ? {
          name: currentUser.displayName,
          mail: currentUser.mail || currentUser.userPrincipalName,
          email: currentUser.mail || currentUser.userPrincipalName,
          color: "#5B6CFF",
          initials: currentUser.displayName.split(" ").filter(Boolean).map(n=>n[0]).join("").slice(0,2).toUpperCase(),
        } : null)} size="sm"/>
        <span style={{fontSize:12, color:"rgba(255,255,255,0.8)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, minWidth:0}}>
          {currentUser ? currentUser.displayName : "-"}
        </span>
      </div>
    </div>
  );
};

// ---- Top bar ----
const TopBar = ({ state, dispatch, onRefresh, refreshing, searchQuery, onSearch, onToggleSidebar }) => {
  const titles = {
    home: "",  // home view has its own page-level greeting/header; no topbar title needed
    all: "All tickets",
    inbox: "All open tickets",
    mine: "My tickets",
    unassigned: "Unassigned",
    resolved: "Closed tickets",
    'fa-ib': "All File Access / Information Barrier tickets",
    trash: "Recycle bin",
    kb: "Knowledge base",
    stats: "Dashboard",
    contacts: "Supplier Contacts",
    tasks: "To-Do List",
    "tasks-mine": "My Tasks",
    "tasks-trash": "Task Recycle Bin",
    "feedback-requests": "Feedback",
    "user-requests":     "User Requests",
    wsm: "WSM Policies",
    tap: "Access Passes",
    "duo-passes": "Duo Passes",
    "evolution-matters": "Evolution Matters",
    "claude-apps": "Claude Apps (Firm)",
    settings: "Settings",
    rota: "IT Rota",
  };
  // Hide ticket-specific controls (search/mailbox/refresh) on views where
  // they make no sense. Name is legacy - really means "non-ticket view".
  const isTasksView = state.view === "home" || state.view === "settings" || state.view === "tasks" || state.view === "tasks-mine" || state.view === "tasks-trash" || state.view === "feedback-requests" || state.view === "user-requests" || state.view === "wsm" || state.view === "tap" || state.view === "duo-passes" || state.view === "contacts" || state.view === "kb" || state.view === "rota" || state.view === "claude-apps" || state.view === "evolution-matters";
  return (
    <div className="topbar" style={state.view === "home" ? { borderBottom: "none" } : undefined}>
      {/* Mobile-only hamburger - hidden on desktop via CSS (.tb-burger). */}
      <button className="tb-burger" onClick={onToggleSidebar} aria-label="Menu">
        <span/><span/><span/>
      </button>
      <span className="title">{titles[state.view]}</span>
      {!isTasksView && <>
        <div className="search">
          <Icon name="search" size={12}/>
          <input
            placeholder="Search tickets…"
            value={searchQuery}
            onChange={e => onSearch(e.target.value)}
          />
          {searchQuery && <button onClick={() => onSearch("")} style={{background:"none",border:"none",cursor:"pointer",padding:"0 4px",color:"var(--text-subtle)",fontSize:14,lineHeight:1}}>×</button>}
        </div>
        <button className="btn" onClick={() => dispatch({type:"openMailbox"})} title="Shared mailbox">
          <Icon name="mail" size={13}/>
          <span style={{fontSize:12}}>Mailbox</span>
          <span style={{width:6, height:6, borderRadius:"50%", background:"var(--st-done)", marginLeft:2}}/>
        </button>
        <button className="btn" title="Refresh" onClick={onRefresh} disabled={refreshing} style={refreshing ? {opacity:0.5} : {}}>
          <Icon name="refresh" size={13} style={refreshing ? {animation:"spin 0.8s linear infinite"} : {}}/>
        </button>
      </>}
    </div>
  );
};

// ---- Bulk action bar ----
const BulkBar = ({ state, dispatch }) => {
  const [popover, setPopover] = React.useState(null);
  // Cross-subject merge chooser modal - null when closed, or
  // { candidates: [{id, subject, created}], defaultPrimaryId } when open.
  // Only shown for the "subjects differ" branch of the merge button; same-
  // subject merges stay one-click with the oldest auto-picked as primary.
  const [mergePicker, setMergePicker] = React.useState(null);
  const refs = { assign: React.useRef(), status: React.useRef() };
  const inTrash = state.view === "trash";

  // Shared merge logic - used by both the same-subject auto path and the
  // chooser modal's Confirm button. Keeps the dispatch wiring in one place.
  const runMerge = (primaryId, allIds) => {
    const sourceIds = allIds.filter(id => id !== primaryId);
    dispatch({type:"mergeDuplicates", primaryId, sourceIds});
    dispatch({type:"clearChecks"});
  };

  return (
    <div className="sel-bar">
      <span>{state.selected.size} selected</span>
      <div className="sep"/>

      {inTrash ? (
        <>
          <button onClick={() => dispatch({type:"restoreTickets"})}>
            <Icon name="refresh" size={11}/> Restore
          </button>
          <div className="sep"/>
          <button className="danger" onClick={() => { if (window.confirm("Permanently delete all items in the recycle bin?")) dispatch({type:"emptyTrash"}); }}>
            <Icon name="trash" size={11}/> Empty recycle bin
          </button>
        </>
      ) : (
        <>
          <button ref={refs.assign} onClick={() => setPopover(popover === "assign" ? null : "assign")}>
            <Icon name="user" size={11}/> Assign
          </button>
          <button ref={refs.status} onClick={() => setPopover(popover === "status" ? null : "status")}>
            <Icon name="check" size={11}/> Status
          </button>
          <button onClick={() => {
            const ids = [...state.selected];
            if (ids.length < 2) {
              alert("Pick 2+ tickets to merge.");
              return;
            }
            // Auto-merge (the in-detail duplicate banner) stays strict on
            // subject matching. Manual merge (this button) accepts any 2+
            // tickets per Alex's feature request 4 Jun 2026.
            const candidates = ids
              .map(id => state.tickets.find(t => t.id === id))
              .filter(Boolean);
            const subjects = candidates.map(t => t.subject.trim().toLowerCase());
            const subjectsMatch = new Set(subjects).size === 1;

            // Oldest = default primary (helpdesk convention). `created` is
            // minsAgo and older tickets are more negative, so ascending sort
            // puts the oldest first.
            const oldestId = candidates.slice().sort((a, b) => {
              const ca = a.created ?? 0, cb = b.created ?? 0;
              if (ca !== cb) return ca - cb;
              return a.id - b.id;
            })[0].id;

            if (subjectsMatch) {
              // Common case - same subject, one click, no friction.
              runMerge(oldestId, ids);
            } else {
              // Cross-subject - open the chooser so the user picks which
              // subject (and therefore which ticket) is kept as primary.
              setMergePicker({
                candidates: candidates.sort((a, b) => (a.created ?? 0) - (b.created ?? 0)),
                primaryId: oldestId,
              });
            }
          }}>
            <Icon name="merge" size={11}/> Merge
          </button>
          <div className="sep"/>
          <button className="danger" onClick={() => dispatch({type:"trashTickets"})}>
            <Icon name="trash" size={11}/> Delete
          </button>
        </>
      )}

      <div className="sep"/>
      <button onClick={() => dispatch({type:"clearChecks"})}><Icon name="close" size={11}/></button>

      {popover === "assign" && (
        <Popover anchorRef={refs.assign} onClose={() => setPopover(null)}>
          <div className="pop-item" onClick={() => { dispatch({type:"bulkAssign", assignee: null}); setPopover(null); }}>
            <span className="av sm empty">·</span> Unassigned
          </div>
          <div className="pop-divider"/>
          {USERS.map(u => (
            <div key={u.id} className="pop-item" onClick={() => { dispatch({type:"bulkAssign", assignee: u.id}); setPopover(null); }}>
              <Avatar user={u} size="sm"/> {u.name}
            </div>
          ))}
        </Popover>
      )}
      {popover === "status" && (
        <Popover anchorRef={refs.status} onClose={() => setPopover(null)}>
          {STATUSES.map(s => (
            <div key={s.id} className="pop-item" onClick={() => { dispatch({type:"bulkStatus", status: s.id}); setPopover(null); }}>
              <span className={`dot ${s.tone}`}/> {s.label}
            </div>
          ))}
        </Popover>
      )}

      {/* Cross-subject merge chooser. Portal'd to document.body so the
          fixed-position overlay isn't constrained by BulkBar's own
          fixed-position container (which created a containing block that
          trapped the modal in the bottom-right corner). */}
      {mergePicker && ReactDOM.createPortal(
        <div className="modal-wrap" onClick={() => setMergePicker(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{width: 560}}>
            <div className="modal-head">
              <h2>Merge tickets - choose primary</h2>
              <button className="btn ghost" style={{marginLeft:"auto"}} onClick={() => setMergePicker(null)}>
                <Icon name="close" size={11}/>
              </button>
            </div>
            <div className="modal-body">
              <p style={{margin:"0 0 14px", color:"var(--text-muted)", fontSize:13}}>
                These tickets have different subjects. The one you pick keeps its subject; the others get folded into its thread.
              </p>
              {mergePicker.candidates.map(t => {
                const isPrimary = mergePicker.primaryId === t.id;
                return (
                  <label
                    key={t.id}
                    onClick={() => setMergePicker(m => ({...m, primaryId: t.id}))}
                    style={{
                      display:"flex", alignItems:"flex-start", gap:10,
                      padding:"10px 12px", marginBottom:6,
                      // Subtle highlight when selected - left accent stripe +
                      // a small lift in background tint, rather than a full
                      // bright teal fill which read as a UI bug.
                      borderLeft:`3px solid ${isPrimary ? "var(--accent)" : "transparent"}`,
                      border:"1px solid var(--line)",
                      borderLeftWidth: 3,
                      borderRadius:6, cursor:"pointer",
                      background: isPrimary ? "var(--bg-sub)" : "transparent",
                      transition:"background 120ms ease, border-color 120ms ease",
                    }}
                  >
                    <input
                      type="radio"
                      name="mergePrimary"
                      checked={isPrimary}
                      onChange={() => setMergePicker(m => ({...m, primaryId: t.id}))}
                      style={{marginTop:3}}
                    />
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontWeight:600, fontSize:13}}>
                        #{t.id} · {t.subject}
                      </div>
                      <div style={{fontSize:11, color:"var(--text-muted)", marginTop:2}}>
                        {(t.requesterName || t.requester || "-")}
                        {t.created != null && ` · ${fmtTime(t.created)} ago`}
                        {isPrimary && <span style={{color:"var(--accent)", fontWeight:600, marginLeft:8}}>← will be kept</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setMergePicker(null)}>Cancel</button>
              <button
                className="btn primary"
                onClick={() => {
                  const allIds = mergePicker.candidates.map(t => t.id);
                  runMerge(mergePicker.primaryId, allIds);
                  setMergePicker(null);
                }}
              >Merge into #{mergePicker.primaryId}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ---- New ticket modal ----
function StaffPicker({ staffList, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  // Trigger rect - captured when opening so the dropdown can render with
  // position:fixed (escapes the modal-body's overflow:auto, which was
  // clipping the dropdown). Re-captured on window scroll/resize so it
  // tracks the trigger if the modal moves.
  const [rect, setRect] = React.useState(null);
  const ref = React.useRef();
  const triggerRef = React.useRef();
  const inputRef = React.useRef();
  const selected = staffList.find(s => s.mail === value);
  const filtered = staffList.filter(s => !query || s.displayName.toLowerCase().includes(query.toLowerCase()));

  // Recompute the trigger rect. Called on open and whenever the page might
  // shift (scroll, resize, modal-body scroll).
  const updateRect = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    setRect(el.getBoundingClientRect());
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      // Clicks inside the trigger area close via the trigger handler. Clicks
      // outside both trigger and dropdown close the popup. The dropdown is
      // rendered outside `ref` (it's position:fixed at the document root
      // visually), so we check both.
      if (ref.current && !ref.current.contains(e.target) &&
          !e.target.closest?.('[data-staffpicker-dropdown="1"]')) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Track scroll / resize while open so the dropdown stays anchored to the
  // trigger as the modal scrolls or the viewport changes. Capture phase
  // so we also catch the modal-body's internal scroll.
  React.useEffect(() => {
    if (!open) return;
    const onChange = () => updateRect();
    window.addEventListener("scroll", onChange, true);
    window.addEventListener("resize", onChange);
    return () => {
      window.removeEventListener("scroll", onChange, true);
      window.removeEventListener("resize", onChange);
    };
  }, [open, updateRect]);

  const openDropdown = () => {
    if (open) { setOpen(false); return; }
    updateRect();
    setOpen(true); setQuery(""); setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={ref} style={{position:"relative"}}>
      <div
        ref={triggerRef}
        onClick={openDropdown}
        style={{
          // Match .field select styling so this dropdown trigger reads as a
          // first-class form input alongside the native selects, not a
          // greyed-out variant of one.
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"6px 8px", border:"1px solid var(--line-strong)", borderRadius:"var(--radius-sm)",
          background:"var(--bg-elev)", cursor:"pointer", fontSize:13,
          color: selected ? "var(--text)" : "var(--text-muted)",
          userSelect:"none",
        }}
      >
        <span>{selected ? selected.displayName : "Select requester…"}</span>
        <span style={{fontSize:10, color:"var(--text-muted)"}}>▾</span>
      </div>

      {open && rect && (
        <div
          data-staffpicker-dropdown="1"
          style={{
            // position:fixed escapes the modal-body overflow:auto that was
            // clipping the dropdown. Coordinates come from the trigger's
            // getBoundingClientRect (viewport-relative, matches fixed).
            position:"fixed",
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
            background:"var(--bg-elev)", border:"1px solid var(--line)",
            borderRadius:"var(--radius-sm)", boxShadow:"var(--shadow-pop)",
          }}
        >
          <div style={{padding:"8px 8px 6px", borderBottom:"1px solid var(--line)"}}>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search staff…"
              style={{width:"100%", padding:"5px 8px", border:"1px solid var(--line)", borderRadius:"var(--radius-sm)", fontSize:12, background:"var(--bg)", color:"var(--text)"}}
            />
          </div>
          <div style={{maxHeight:220, overflowY:"auto"}}>
            {filtered.map(s => (
              <div
                key={s.id}
                onMouseDown={(e) => { e.preventDefault(); onChange(s.mail); setOpen(false); }}
                style={{
                  padding:"8px 12px", fontSize:13, cursor:"pointer",
                  background: s.mail === value ? "var(--accent-soft)" : "",
                  borderBottom:"1px solid var(--line)",
                }}
                onMouseEnter={e => e.currentTarget.style.background="var(--bg-hover)"}
                onMouseLeave={e => e.currentTarget.style.background = s.mail === value ? "var(--accent-soft)" : ""}
              >
                <span style={{fontWeight: 500}}>{s.displayName}</span>
                {s.department && <span style={{fontSize:11, color:"var(--text-subtle)", marginLeft:8}}>{s.department}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const NewTicketModal = ({ dispatch, staffList }) => {
  const [subject, setSubject] = React.useState("");
  const [body, setBody] = React.useState("");
  const [requester, setRequester] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [subcat, setSubcat] = React.useState("");
  const [priority, setPriority] = React.useState("medium");
  const [location, setLocation] = React.useState("");

  const submit = () => {
    if (!subject.trim()) return;
    dispatch({type:"createTicket", subject, body, requester, category, subcat, priority, location, assignee: null});
  };

  return (
    <div className="modal-wrap">
      {/* Backdrop click intentionally does NOT close - easy to lose form data. */}
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h2>New ticket</h2>
          <button className="btn ghost" style={{marginLeft:"auto"}} onClick={() => dispatch({type:"closeNewTicket"})}>
            <Icon name="close" size={11}/>
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Subject</label>
            <input autoFocus value={subject} onChange={e => setSubject(e.target.value)} placeholder="What's the issue?"/>
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 10}}>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={e => { const cat = e.target.value; setCategory(cat); setSubcat((SUBCATS[cat] || [])[0] || ""); }}>
                <option value="">- Select -</option>
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Subcategory</label>
              <select value={subcat} onChange={e => setSubcat(e.target.value)} disabled={!category || !(SUBCATS[category]?.length)}>
                <option value="">- Select -</option>
                {(SUBCATS[category] || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}>
                {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          </div>
          {/* Requester + Location on one row. Putting Location higher in the
              modal gives the browser room to open the native dropdown
              downward (when it sat lower, Chrome was flipping it upward
              and covering the other fields). Native <select> is otherwise
              preferred - keyboard nav, OS theming, no custom code. */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap: 10}}>
            <div className="field">
              <label>Requester</label>
              <StaffPicker staffList={staffList} value={requester} onChange={setRequester}/>
            </div>
            <div className="field">
              <label>Location</label>
              <select value={location} onChange={e => setLocation(e.target.value)}>
                <option value="">- Select -</option>
                {(window.LOCATIONS || LOCATIONS || []).map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Description</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Describe the issue, who's affected, anything tried already…"/>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={() => dispatch({type:"closeNewTicket"})}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!subject.trim() || !requester}>Create ticket</button>
        </div>
      </div>
    </div>
  );
};

// ---- New article modal ----
// Parse a plain-text body into the block array format the KB renderer expects.
//   "## Heading"     -> ["h2", "Heading"]
//   "- item"         -> grouped into ["ul", ["item", ...]]
//   "1. item"        -> grouped into ["ol", ["item", ...]]
//   anything else    -> ["p", "text"]
const parseArticleBody = (raw) => {
  const lines = raw.split(/\r?\n/);
  const blocks = [];
  let ulBuf = null;
  let olBuf = null;
  const flushLists = () => {
    if (ulBuf) { blocks.push(["ul", ulBuf]); ulBuf = null; }
    if (olBuf) { blocks.push(["ol", olBuf]); olBuf = null; }
  };
  for (const lineRaw of lines) {
    const line = lineRaw.trimEnd();
    if (!line.trim()) { flushLists(); continue; }
    if (line.startsWith("## ")) { flushLists(); blocks.push(["h2", line.slice(3).trim()]); continue; }
    if (line.startsWith("# "))  { flushLists(); blocks.push(["h2", line.slice(2).trim()]); continue; }
    if (/^[-*]\s+/.test(line)) {
      if (olBuf) flushLists();
      if (!ulBuf) ulBuf = [];
      ulBuf.push(line.replace(/^[-*]\s+/, ""));
      continue;
    }
    if (/^\d+[.)]\s+/.test(line)) {
      if (ulBuf) flushLists();
      if (!olBuf) olBuf = [];
      olBuf.push(line.replace(/^\d+[.)]\s+/, ""));
      continue;
    }
    flushLists();
    blocks.push(["p", line.trim()]);
  }
  flushLists();
  return blocks;
};

// Image upload handler for Quill's toolbar 'image' button.
// Overrides Quill's default (which only takes a URL) with a file picker that
// uploads via POST /api/kb/upload-image and inserts the resulting blob URL.
// `quillRef` is a React ref - we resolve it at click time, not at handler-bind
// time, because the editor may not yet exist when the toolbar config is built.
const makeImageHandler = (quillRef) => () => {
  const quill = quillRef.current;
  if (!quill) return;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png,image/jpeg,image/webp,image/gif';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image too large (max 5 MB).'); return; }
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
      alert('Only PNG, JPG, WebP, or GIF images are allowed.'); return;
    }
    // Placeholder while uploading so the user sees something happen
    const range = quill.getSelection(true);
    const placeholder = ' [uploading…] ';
    quill.insertText(range.index, placeholder, 'user');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = reject;
        r.onload = () => resolve(String(r.result).split(',')[1]);
        r.readAsDataURL(file);
      });
      const res = await window.IT_AUTH.api('POST', '/kb/upload-image', {
        filename: file.name, mimeType: file.type, data: base64,
      }, 'kbImage');
      quill.deleteText(range.index, placeholder.length, 'user');
      quill.insertEmbed(range.index, 'image', res.url, 'user');
      quill.setSelection(range.index + 1);
    } catch (e) {
      quill.deleteText(range.index, placeholder.length, 'user');
      alert('Upload failed: ' + (e?.message || 'unknown error'));
    }
  };
  input.click();
};

// Legacy block array → HTML string. Used when editing imported (pre-Quill)
// articles so they can be displayed in Quill and saved back as HTML.
const blocksToHtml = (blocks) => {
  if (!Array.isArray(blocks)) return '';
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return blocks.map(block => {
    if (!Array.isArray(block)) return '';
    const [type, content, ...rest] = block;
    if (type === 'h2') return `<h2>${esc(content)}</h2>`;
    if (type === 'p') {
      if (Array.isArray(content) && content.length > 0 && typeof content[0] !== 'string') {
        return `<p>${content.map(c => Array.isArray(c) ? `<code>${esc(c[1])}</code>` : esc(c)).join('')}</p>`;
      }
      return `<p>${[content, ...rest].map(c => Array.isArray(c) ? `<code>${esc(c[1])}</code>` : esc(c)).join('')}</p>`;
    }
    if (type === 'ul') return `<ul>${(content || []).map(li => `<li>${esc(li)}</li>`).join('')}</ul>`;
    if (type === 'ol') return `<ol>${(content || []).map(li => `<li>${esc(li)}</li>`).join('')}</ol>`;
    return '';
  }).join('');
};

const EditArticleModal = ({ dispatch, article }) => {
  const [title, setTitle] = React.useState(article.title || '');
  const [category, setCategory] = React.useState(article.category || 'SOFTWARE');
  const editorContainerRef = React.useRef(null);
  const quillRef = React.useRef(null);

  React.useEffect(() => {
    if (!editorContainerRef.current || quillRef.current || !window.Quill) return;
    if (window.QuillResizeImage && !window.Quill.imports['modules/resize']) {
      window.Quill.register('modules/resize', window.QuillResizeImage);
    }
    quillRef.current = new window.Quill(editorContainerRef.current, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers: { image: makeImageHandler(quillRef) },
        },
        resize: window.QuillResizeImage ? { locale: {} } : undefined,
      },
    });
    // Pre-fill: HTML string (new format) loads directly; block array (legacy) gets converted.
    const initialHtml = Array.isArray(article.body) ? blocksToHtml(article.body) : (article.body || '');
    quillRef.current.root.innerHTML = initialHtml;
    return () => { quillRef.current = null; };
  }, []);

  const submit = () => {
    if (!title.trim()) return;
    const html = quillRef.current ? quillRef.current.root.innerHTML : '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plain = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    const words = plain.split(' ').filter(Boolean);
    const summary = plain ? (words.slice(0, 20).join(' ') + (words.length > 20 ? '…' : '')) : '-';
    dispatch({
      type: "updateArticle",
      id: article.id,
      updates: {
        title: title.trim(),
        category,
        summary,
        body: html,
      },
    });
    dispatch({ type: "closeEditArticle" });
  };

  return (
    <div className="modal-wrap">
      <div className="modal" onClick={e => e.stopPropagation()} style={{width: 760, maxWidth: "94vw"}}>
        <div className="modal-head">
          <h2>Edit article</h2>
          <div style={{marginLeft:"auto"}}>
            <button className="btn ghost" onClick={() => dispatch({type:"closeEditArticle"})}><Icon name="close" size={11}/></button>
          </div>
        </div>
        <div className="modal-body" style={{maxHeight: "75vh", overflowY: "auto"}}>
          <div className="field">
            <label>Title</label>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)}/>
          </div>
          <div className="field">
            <label>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Notes</label>
            <div ref={editorContainerRef} style={{minHeight: 340, fontSize: 14, lineHeight: 1.55}}/>
            <div style={{fontSize: 11, color:"var(--text-subtle)", marginTop: 6}}>
              Use the toolbar for formatting, links, and images.
            </div>
          </div>
          <KBAttachmentsBlock articleId={article.id}/>
        </div>
        <div className="modal-foot">
          <button className="btn danger" onClick={() => {
            if (confirm(`Delete article "${article.title}"? This cannot be undone.`)) {
              dispatch({ type: "deleteArticle", id: article.id });
              dispatch({ type: "closeEditArticle" });
            }
          }} style={{marginRight: 'auto'}}>
            <Icon name="trash" size={11}/> Delete
          </button>
          <button className="btn" onClick={() => dispatch({type:"closeEditArticle"})}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!title.trim()}>
            <Icon name="check" size={11}/> Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

const NewArticleModal = ({ dispatch }) => {
  const [title, setTitle] = React.useState("");
  const [category, setCategory] = React.useState("SOFTWARE");
  const editorContainerRef = React.useRef(null);
  const quillRef = React.useRef(null);

  // Mount Quill once after the modal renders. The editor stores its own state
  // in the DOM (uncontrolled) - we read its HTML at submit time. Quill is
  // loaded from CDN in index.html; the safety net below handles a failed load.
  React.useEffect(() => {
    if (!editorContainerRef.current || quillRef.current) return;
    if (!window.Quill) {
      console.warn("[NewArticle] Quill not loaded - falling back to a plain textarea.");
      return;
    }
    // Register the image-resize module once (no-op if already done)
    if (window.QuillResizeImage && !window.Quill.imports['modules/resize']) {
      window.Quill.register('modules/resize', window.QuillResizeImage);
    }
    quillRef.current = new window.Quill(editorContainerRef.current, {
      theme: 'snow',
      placeholder: 'What does this article cover? Steps, links, screenshots…',
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers: { image: makeImageHandler(quillRef) },
        },
        // Enable click-to-drag image resize handles
        resize: window.QuillResizeImage ? { locale: {} } : undefined,
      },
    });
    return () => { quillRef.current = null; };
  }, []);

  const submit = () => {
    if (!title.trim()) return;
    // Read HTML from Quill (or empty if Quill failed to load)
    const html = quillRef.current ? quillRef.current.root.innerHTML : '';
    // Plain-text version of the HTML, for the summary
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const plain = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    const words = plain.split(' ').filter(Boolean);
    const summary = plain ? (words.slice(0, 20).join(' ') + (words.length > 20 ? '…' : '')) : '-';
    dispatch({
      type: "createArticle",
      title: title.trim(),
      category,
      tags: [],
      summary,
      body: html, // store as HTML - DOMPurify-sanitised at render time
      status: "published",
    });
  };

  return (
    <div className="modal-wrap">
      <div className="modal" onClick={e => e.stopPropagation()} style={{width: 760, maxWidth: "94vw"}}>
        <div className="modal-head">
          <h2>New knowledge base article</h2>
          <div style={{marginLeft:"auto"}}>
            <button className="btn ghost" onClick={() => dispatch({type:"closeNewArticle"})}><Icon name="close" size={11}/></button>
          </div>
        </div>
        <div className="modal-body" style={{maxHeight: "75vh", overflowY: "auto"}}>
          <div className="field">
            <label>Title</label>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. How to reset Duo when a user loses their phone"/>
          </div>
          <div className="field">
            <label>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Notes</label>
            {/* Quill mounts here; toolbar appears on top, editor below. */}
            <div ref={editorContainerRef} style={{minHeight: 340, fontSize: 14, lineHeight: 1.55}}/>
            <div style={{fontSize: 11, color:"var(--text-subtle)", marginTop: 6}}>
              Use the toolbar for formatting, links, and images. To attach files (e.g. PDFs), save the article first - the Attachments box appears on the article page and in the edit window.
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <span style={{marginRight: "auto", fontSize: 11, color: "var(--text-subtle)"}}>
            {title.trim() ? "Ready to save." : "Add a title to save."}
          </span>
          <button className="btn" onClick={() => dispatch({type:"closeNewArticle"})}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!title.trim()}>
            <Icon name="check" size={11}/> Save article
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Mailbox drawer ----
const MailboxDrawer = ({ state, dispatch, onSync }) => {
  const pending = state.mailbox.filter(m => !m.ticketId);
  const converted = state.mailbox.filter(m => m.ticketId);
  const lastSync = "2 min ago";

  return (
    <>
      <div className="backdrop" style={{zIndex: 90, background:"oklch(0% 0 0 / 0.2)"}} onClick={() => dispatch({type:"closeMailbox"})}/>
      <div className="mailbox-drawer">
        <div className="mailbox-head">
          <div className="row">
            <Icon name="inbox" size={16}/>
            <div style={{flex:1}}>
              <h2>Shared mailbox</h2>
              <div className="sub">triage@leeandthompson.com</div>
            </div>
            <button className="btn" title="Sync now" onClick={onSync}><Icon name="refresh" size={12}/> Sync now</button>
            <button className="btn ghost" onClick={() => dispatch({type:"closeMailbox"})}><Icon name="close" size={11}/></button>
          </div>
          <div className="row" style={{marginTop:10, fontSize: 12, color:"var(--text-muted)"}}>
            <span style={{display:"flex", alignItems:"center", gap:6}}>
              <span style={{width:8, height:8, borderRadius:"50%", background:"var(--st-done)"}}/>
              Connected · Auto-sync every 2 min · last sync <b style={{color:"var(--text)", marginLeft:4}}>{lastSync}</b>
            </span>
          </div>
        </div>

        <div className="mailbox-body">
          {pending.length > 0 && (
            <>
              <div className="mailbox-section-head">
                Pending conversion <span className="pill">{pending.length}</span>
                <span style={{marginLeft:"auto", color:"var(--text-subtle)", fontSize:10, letterSpacing:0, textTransform:"none", fontWeight:400}}>auto-convert in ~30s</span>
              </div>
              {pending.map(m => (
                <div key={m.id} className="mail-item pending">
                  <div style={{minWidth:0}}>
                    <div className="from">From <b>{m.from}</b></div>
                    <div className="subj">{m.subject}</div>
                    <div className="preview">{m.body}</div>
                  </div>
                  <div className="ts">{fmtTime(m.received)} ago</div>
                  <div className="actions">
                    <button className="btn primary" onClick={() => dispatch({type:"convertEmailToTicket", emailId: m.id, localId: -Date.now()})}>
                      <Icon name="check" size={11}/> Convert to ticket
                    </button>
                    <button className="btn ghost"><Icon name="archive" size={11}/> Ignore</button>
                  </div>
                </div>
              ))}
            </>
          )}

          <div className="mailbox-section-head">
            Recently converted <span className="pill">{converted.length}</span>
          </div>
          {converted.map(m => (
            <div key={m.id} className="mail-item">
              <div style={{minWidth:0}}>
                <div className="from">From <b>{m.from.replace(/<.+>/, "").trim()}</b></div>
                <div className="subj">{m.subject}</div>
              </div>
              <div className="ts">{fmtTime(m.received)} ago</div>
              <div className="actions">
                <span className="link" onClick={() => { dispatch({type:"closeMailbox"}); dispatch({type:"select", id: m.ticketId}); }}>
                  → Ticket #{m.ticketId}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mailbox-foot">
          <strong>How it works:</strong> drag emails into the <code style={{fontFamily:"var(--mono)"}}>triage@leeandthompson.com</code> shared mailbox in Outlook. The mailbox is polled every 2 minutes - any unread emails are automatically converted into new tickets. Once you've resolved a ticket, mark it closed here.
        </div>
      </div>
    </>
  );
};

// ---- Login screen ----
function LoginScreen({ onLogin, error, loading }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      height: "100vh", background: "#f3f2f1", flexDirection: "column",
      fontFamily: "'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: "#ffffff", padding: "44px 44px 36px",
        width: 400, boxSizing: "border-box",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        textAlign: "center",
      }}>
        {/* Triage primary lockup - light-background variant (dark "AGE", muted subtitle) */}
        <svg width="220" height="88" viewBox="0 0 240 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Triage" style={{display: "block", margin: "0 auto 24px"}}>
          <rect x="24" y="24" width="30" height="23" rx="5" fill="#0D9488" fillOpacity="0.35"/>
          <rect x="16" y="34" width="30" height="23" rx="5" fill="#0D9488"/>
          <line x1="22" y1="44" x2="42" y2="44" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
          <line x1="22" y1="50" x2="34" y2="50" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
          <text x="66" y="52" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif" fontSize="32" fontWeight="700" letterSpacing="2">
            <tspan fill="#0D9488">TRI</tspan>
            <tspan fill="#1F2D34">AGE</tspan>
          </text>
          <text x="68" y="70" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif" fontSize="11" fontWeight="500" letterSpacing="3" fill="#6B7785">IT Helpdesk</text>
        </svg>

        <div style={{fontSize: 24, fontWeight: 600, color: "#1b1b1b", marginBottom: 12}}>Sign in</div>
        <div style={{fontSize: 13, color: "#444", marginBottom: 24}}>
          Use your <strong>Lee &amp; Thompson</strong> Microsoft account to access Triage.
        </div>

        {error && (
          <div style={{
            fontSize: 12, color: "#a4262c", background: "#fde7e9",
            border: "1px solid #f1707b", padding: "8px 12px",
            marginBottom: 16,
          }}>
            {error === "access_denied"
              ? "Your account doesn't have access to Triage. Contact the IT team if you think this is a mistake."
              : error}
          </div>
        )}

        <button
          onClick={onLogin}
          disabled={loading}
          style={{
            width: "100%", padding: "10px 0", fontSize: 14,
            background: loading ? "#999" : "#0067b8", color: "#fff",
            border: "none", cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "'Segoe UI', sans-serif", fontWeight: 600,
            marginBottom: 16,
          }}
        >
          {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>

        <div style={{fontSize: 11, color: "#888", borderTop: "1px solid #e0e0e0", paddingTop: 16}}>
          This app uses Microsoft Entra ID for authentication. Your credentials are never stored by this application.
        </div>
      </div>

      <div style={{marginTop: 20, fontSize: 11, color: "#888", display: "flex", gap: 16}}>
        <a href="https://privacy.microsoft.com" target="_blank" style={{color: "#888"}}>Privacy</a>
        <a href="https://www.microsoft.com/terms" target="_blank" style={{color: "#888"}}>Terms of use</a>
        <span>© {new Date().getFullYear()} Microsoft</span>
      </div>
    </div>
  );
}

// Boot
function Root() {
  const [user, setUser] = React.useState(null);
  const [isITUser, setIsITUser] = React.useState(false);
  const [staffList, setStaffList] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [teamVersion, setTeamVersion] = React.useState(0);
  const isPhone = useIsPhone();
  // Whether the page was LOADED at /access-passes. Captured once at mount so that
  // navigating to Access Passes inside the full app doesn't swap layouts - only a
  // direct load of that URL gets the stripped phone shell.
  const [accessPassesLanding] = React.useState(
    () => typeof window !== 'undefined' &&
      window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/access-passes',
  );

  // When Triage is opened at /access-passes, brand the "Add to Home Screen"
  // install as its own thing: the TAP icon + name + manifest, so it installs
  // separately from the main Triage app. iOS reads these head tags at add-time,
  // so setting them on load is enough.
  React.useEffect(() => {
    if (!accessPassesLanding) return;
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setLink('apple-touch-icon', '/tap-icon.png');
    setLink('manifest', '/manifest-tap.webmanifest');
    setMeta('apple-mobile-web-app-title', 'TAP');
  }, [accessPassesLanding]);

  // /requests is the user-facing portal - chromeless, available to any
  // Entra-authenticated user in the tenant regardless of IT-group
  // membership. Detected once at mount from the URL; the portal has no
  // in-page routing so we don't need to react to navigation.
  const isPortalPath = typeof window !== 'undefined' &&
    (window.location.pathname === '/requests' || window.location.pathname.startsWith('/requests/'));

  // /oauth-callback is where NetDocuments redirects after the user signs
  // in. It's a chromeless page like /requests - reads the code + state
  // from the URL and POSTs them to the backend to complete the exchange.
  // IT-only (any non-IT user landing here gets bounced to /requests).
  const isNdCallbackPath = typeof window !== 'undefined' &&
    window.location.pathname === '/oauth-callback';

  // /evomatters is the chromeless standalone Evolution Matters page - shareable
  // outside IT (e.g. the CFO). Non-IT users are allowed to stay here (the API
  // allowlist decides what they can actually see) rather than being bounced to
  // the portal.
  const isEvoPath = typeof window !== 'undefined' &&
    window.location.pathname === '/evomatters';

  // Loads profile + IT-group gate + staff once an MSAL account exists.
  // Non-IT users no longer see access_denied - they get the portal
  // instead. IT users get the full app as before.
  const loadSignedInUser = async () => {
    const [me, isIT, staff] = await Promise.all([
      window.IT_AUTH.getCurrentUser(),
      window.IT_AUTH.checkITMember(),
      window.IT_AUTH.getStaff().catch(() => []),
    ]);
    setIsITUser(isIT);
    if (!isIT) {
      // Non-IT: park them on /requests regardless of where they landed,
      // so a refresh keeps them on the portal - except on the standalone
      // /evomatters page, which non-IT allowlisted users are meant to reach.
      if (!isPortalPath && !isEvoPath) window.history.replaceState(null, '', '/requests');
      setUser(me);
      return true;
    }
    const match = USERS.find(u => u.email.toLowerCase() === (me.mail || me.userPrincipalName || "").toLowerCase());
    if (match) setMeId(match.id);
    window.STAFF_LIST = staff;
    setStaffList(staff);
    setUser(me);
    // Fetch job titles using delegated token (same path as login)
    window.IT_AUTH.getITTeamProfiles(USERS.map(u => u.email)).then(profiles => {
      profiles.forEach(p => {
        const email = (p.mail || p.userPrincipalName || "").toLowerCase();
        const u = USERS.find(u => u.email.toLowerCase() === email);
        if (u && p.jobTitle) u.title = p.jobTitle;
      });
      USERS.sort((a, b) => a.name.localeCompare(b.name));
      setTeamVersion(v => v + 1);
    }).catch(() => {});
    return true;
  };

  React.useEffect(() => {
    const restoreSession = async () => {
      try {
        let hasSession = await window.IT_AUTH.init();
        if (!hasSession) {
          // No cached account this browser session - try silent SSO against
          // the existing Microsoft 365 session (hidden iframe, no UI).
          try {
            await window.IT_AUTH.ssoSilent();
            hasSession = true;
          } catch (silentErr) {
            // Silent SSO couldn't proceed (iframe blocked, no session, etc).
            // Auto-redirect to Microsoft ONCE per tab-session - with an
            // active 365 session this round-trips without any prompt. The
            // guard prevents redirect loops; if we come back still signed
            // out, the manual sign-in button shows as the fallback.
            if (!sessionStorage.getItem("triage:auto-signin-attempted")) {
              sessionStorage.setItem("triage:auto-signin-attempted", "1");
              await window.IT_AUTH.signIn(); // navigates away
              return;
            }
          }
        }
        if (hasSession) {
          sessionStorage.removeItem("triage:auto-signin-attempted");
          await loadSignedInUser();
        }
      } catch (e) {
        // session expired or invalid - fall through to login screen
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  // Manual fallback - only reachable if automatic sign-in failed. signIn()
  // redirects the tab to Microsoft; on return, restoreSession picks the
  // account up via handleRedirectPromise, so no post-login code runs here.
  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.setItem("triage:auto-signin-attempted", "1");
      await window.IT_AUTH.signIn(); // navigates away
    } catch (e) {
      setError(e.message || "Sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10, height:"100vh", background:"#f3f2f1", fontFamily:"'Segoe UI', sans-serif", textAlign:"center", padding:"0 24px"}}>
      <div style={{color:"#444", fontSize:15, fontWeight:600}}>Loading Triage…</div>
      <div style={{color:"#888", fontSize:13}}>Signing you in with your Microsoft account. This may take a few seconds.</div>
    </div>
  );
  if (!user) return <LoginScreen onLogin={handleLogin} error={error} loading={loading}/>;
  // OAuth callback from NetDocuments - chromeless, IT-only. Non-IT users
  // shouldn't land here (they can't approve requests either) so bounce
  // them to the portal.
  if (isNdCallbackPath) {
    if (!isITUser) { window.location.replace('/requests'); return null; }
    return <NdOAuthCallback/>;
  }
  // Standalone Evolution Matters page - chromeless, reachable by IT and by
  // non-IT allowlisted users (the API enforces who can actually load data).
  if (isEvoPath) return <EvolutionMattersStandalone/>;
  // Non-IT users only ever see the portal. IT users see the portal too if
  // they explicitly hit /requests (useful for testing) and the full app
  // otherwise.
  if (isPortalPath || !isITUser) return <RequestsPortal user={user}/>;
  // Stripped Access Passes shell (no sidebar, nothing else) ONLY when a phone
  // user loaded the /access-passes URL directly. Opening triage.leeandthompson.com
  // normally on a phone loads the full responsive app like any other device.
  if (isPhone && accessPassesLanding) return <PhoneApp/>;
  return <App currentUser={user} staffList={staffList} teamVersion={teamVersion}/>;
}

export { Root };
